import { useState, useEffect, useRef } from "react"
import { FileUp } from "lucide-react"
import { format } from "date-fns"
import FloatingInput from "../components/Admin/FloatingInput"
import toast from 'react-hot-toast'
import AttachmentPreviewModal from "../components/Common/AttachmentPreviewModal"
import {
  DEFAULT_ALLOWED_UPLOAD_ACCEPT,
  validateUploadFile,
} from "../components/Common/uploadValidation"
import {
  uploadFileToR2,
  createInvestmentAttachment,
  deleteInvestmentAttachment,
  getInvestmentAttachmentUrl,
  getInvestmentAttachmentDownloadUrl,
} from "../services/proofApi"
import api, { payrollService, authService, employeeService, API_URL } from "../services/api"
import { getNumberRangeError, getPanError, getAddressError } from "../utils/investmentValidation"

// const FloatingInput = ({ id, label, value, onChange, type = "text", required = false }) => (
//   <div className="relative">
//     <input
//       id={id}
//       type={type}
//       value={value}
//       onChange={onChange}
//       placeholder=" "
//       required={required}
//       className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-none border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
//     />
//     <label
//       htmlFor={id}
//       className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
//     >
//       {label}
//       {required && <span className="text-red-500">*</span>}
//     </label>
//   </div>
// )

// Simplified SectionUpload widget for Employee (upload + preview only)
const SectionUpload = ({
  sectionName,
  onUpload,
  onRemove,
  disabled = false,
  isSectionLevel = false,
  initialAttachmentId,
  initialFileName,
  initialFileSize,
  initialStatus = "pending",
  initialRejectReason = "",
  onViewAttachment,
  infoText,
}) => {
  const [file, setFile] = useState(
    initialFileName
      ? { name: initialFileName, size: initialFileSize || 0, type: "" }
      : null
  )
  const [photoModal, setPhotoModal] = useState({ isOpen: false, photo: null, fileName: "" })
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [attachmentId, setAttachmentId] = useState(initialAttachmentId || null)
  const [uploadError, setUploadError] = useState("")
  const [status, setStatus] = useState(initialStatus) // pending, approved, rejected
  const [rejectReason, setRejectReason] = useState(initialRejectReason)
  const [showInfo, setShowInfo] = useState(false)

  // Keep internal state in sync when initial props change (e.g., after attachments load or FY/tab change)
  useEffect(() => {
    if (initialFileName) {
      setFile({ name: initialFileName, size: initialFileSize || 0, type: "" })
      if (initialAttachmentId) {
        setAttachmentId(initialAttachmentId)
      }
    }
  }, [initialFileName, initialFileSize, initialAttachmentId])

  // Keep status in sync with backend-provided initialStatus
  useEffect(() => {
    setStatus(initialStatus || "pending")
  }, [initialStatus])

  // Keep reject reason in sync with backend-provided initialRejectReason
  useEffect(() => {
    setRejectReason(initialRejectReason || "")
  }, [initialRejectReason])

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0]
    if (!selectedFile) return

    // If the same file name is selected again for this field, block and show a clear message
    if (file && selectedFile.name === file.name) {
      toast.error(`You already uploaded '${selectedFile.name}' for '${sectionName}'. Please choose a different file or rename it.`)
      return
    }

    const validation = validateUploadFile(selectedFile)
    if (!validation.ok) {
      toast.error(validation.error)
      return
    }

    // Clear any previous error state before a new upload
    setUploadError("")

    setFile(selectedFile)
    setShowRemoveConfirm(false)
    setStatus("pending")

    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoModal({
          isOpen: true,
          photo: reader.result,
          fileName: selectedFile.name,
        })
      }
      reader.readAsDataURL(selectedFile)
    }

    if (typeof onUpload === "function") {
      try {
        const result = await onUpload({
          file: selectedFile,
          sectionName,
          isSectionLevel,
        })
        if (result && result.attachmentId) {
          setAttachmentId(result.attachmentId)
        }
      } catch (err) {
        console.error("Failed to upload proof:", err)
        toast.error(`Failed to upload proof for '${sectionName}' (${selectedFile.name}). Please try again.`)
        setUploadError("Failed to upload proof. Please try again.")
        setFile(null)
        setAttachmentId(null)
      }
    }
  }

  const handleViewPhoto = () => {
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoModal({
          isOpen: true,
          photo: reader.result,
          fileName: file.name,
        })
      }
      reader.readAsDataURL(file)
    }
  }

  const closePhotoModal = () => {
    setPhotoModal({ isOpen: false, photo: null, fileName: "" })
  }

  const downloadPhoto = () => {
    if (photoModal.photo) {
      const link = document.createElement("a")
      link.href = photoModal.photo
      link.download = photoModal.fileName || "document"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const displayName = file?.name || initialFileName;
  const displaySizeBytes =
    file?.size != null ? file.size : initialFileSize != null ? initialFileSize : 0;

  return (
    <div className="mt-2 p-2 border border-gray-200 rounded-none bg-gray-50 relative">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="relative">
              <input
                type="file"
                accept={DEFAULT_ALLOWED_UPLOAD_ACCEPT}
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                id={`file-upload-${sectionName}`}
                disabled={disabled}
              />
              <label
                htmlFor={`file-upload-${sectionName}`}
                className={`w-6 h-6 text-purple-600 bg-purple-50 border border-purple-200 rounded-none hover:bg-purple-100 flex items-center justify-center cursor-pointer ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title={file ? "Replace Document" : "Upload Document"}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </label>
            </div>

            {attachmentId && status !== "rejected" && typeof onViewAttachment === "function" && (
              <button
                type="button"
                onClick={() => onViewAttachment(attachmentId, file?.name || initialFileName || sectionName)}
                className={`w-6 h-6 text-blue-600 bg-blue-50 border border-blue-200 rounded-none flex items-center justify-center ${
                  disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-100"
                }`}
                title="View Document"
                disabled={disabled}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            )}

            {(file || attachmentId) && (
              <button
                type="button"
                className={`w-6 h-6 text-gray-600 bg-gray-100 border border-gray-200 rounded-none flex items-center justify-center ${
                  disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-red-50 hover:text-red-600"
                }`}
                title="Remove Document"
                onClick={() => setShowRemoveConfirm(true)}
                disabled={disabled}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {infoText && (
            <div className="relative">
              <button
                type="button"
                className="w-6 h-6 text-gray-600 bg-gray-100 border border-gray-200 rounded-none flex items-center justify-center hover:bg-gray-200"
                title="More information"
                onClick={() => setShowInfo((prev) => !prev)}
                disabled={disabled}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6a9 9 0 110 18 9 9 0 010-18z" />
                </svg>
              </button>
              {showInfo && (
                <div className="absolute z-20 mt-1 w-64 max-w-xs p-2 bg-white border border-gray-300 shadow-lg text-xs text-gray-700 rounded-none">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-semibold truncate mr-2">{sectionName}</span>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-800"
                      onClick={() => setShowInfo(false)}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="whitespace-pre-line break-words">{infoText}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Middle: filename/size when pending/approved, or reason when rejected */}
        <div className="flex-1 min-w-0">
          {status === "rejected" && rejectReason ? (
            <div
              className="text-xs text-red-700 truncate text-center md:text-left"
              title={rejectReason}
            >
              Reason: {rejectReason}
            </div>
          ) : (
            displayName && status !== "rejected" && (
              <div className="text-xs text-gray-600 truncate text-center md:text-left">
                {displayName}
                {displaySizeBytes
                  ? ` (${(displaySizeBytes / 1024 / 1024).toFixed(2)} MB)`
                  : ""}
              </div>
            )
          )}
        </div>

        {/* Status + reject reason (if any) on the right */}
        {(status && status !== "pending") || (status === "rejected" && rejectReason) ? (
          <div className="ml-2 flex flex-col items-end max-w-xs">
            {status && status !== "pending" && (
              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  status === "approved"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {status === "approved" ? (
                  <span className="inline-flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Approved
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Rejected
                  </span>
                )}
              </span>
            )}

            {/* Reason now shown in the middle area when rejected */}
          </div>
        ) : null}
      </div>
      {uploadError && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1">
          {uploadError}
        </div>
      )}

      {showRemoveConfirm && (file || attachmentId) && (
        <div className="absolute right-2 top-2 z-10 bg-white border border-red-500 text-xs shadow-md rounded-none px-3 py-2 max-w-xs">
          <p className="text-red-700 mb-2">
            Remove this document from this field?
          </p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="px-2 py-1 bg-red-600 text-white text-xs rounded-none hover:bg-red-700"
              onClick={async () => {
                const currentId = attachmentId
                setFile(null)
                setAttachmentId(null)
                setShowRemoveConfirm(false)

                if (currentId && typeof onRemove === "function") {
                  try {
                    await onRemove(currentId)
                  } catch (err) {
                    console.error("Failed to remove proof attachment:", err)
                    toast.error("Failed to remove proof attachment. Please try again.")
                  }
                }
              }}
              disabled={disabled}
            >
              Yes
            </button>
            <button
              type="button"
              className="px-2 py-1 border border-gray-300 text-gray-700 bg-white text-xs rounded-none hover:bg-gray-50"
              onClick={() => setShowRemoveConfirm(false)}
            >
              No
            </button>
          </div>
        </div>
      )}

      {photoModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-none p-6 max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {photoModal.fileName || "Document Preview"}
              </h3>
              <button onClick={closePhotoModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex justify-center mb  -4">
              {photoModal.photo ? (
                <img
                  src={photoModal.photo}
                  alt="Document Preview"
                  className="max-w-full max-h-96 object-contain rounded-none"
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik01MCAxMDAgQzUwIDcyLjM4NTggNzIuMzg1OCA1MCAxMDAgNTAgQzEyNy42MTQgNTAgMTUwIDcyLjM4NTggMTUwIDEwMCBDMTUwIDEyNy42MTQgMTI3LjYxNCAxNTAgMTAwIDE1MCBDNzIuMzg1OCAxNTAgNTAgMTI3LjYxNCA1MCAxMDBaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0xMDAgMTMwIEM4OS4wNTQ0IDEzMCA4MCAxMjAuOTQ1NiA4MCAxMTAgQzgwIDk5LjA1NDQgODkuMDU0NCA5MCAxMDAgOTAgQzExMC45NDYgOTAgMTIwIDk5LjA1NDQgMTIwIDExMCBDMTIwIDEyMC45NDU2IDExMC45NDYgMTMwIDEwMCAxMzBaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0xMDAgMTQwIEM4OS4wNTQ0IDE0MCA4MCAxMzAuOTQ1NiA4MCAxMjAgQzgwIDEwOS4wNTQ0IDg5LjA1NDQgMTAwIDEwMCAxMDAgQzExMC45NDYgMTAwIDEyMCAxMDkuMDU0NCAxMjAgMTIwIEMxMjAgMTMwLjk0NTYgMTEwLjk0NiAxNDAgMTAwIDE0MFoiIGZpbGw9IiM5QjlCQTAiLz4KPC9zdmc+"
                  }}
                />
              ) : (
                <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg className="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
              )}
            </div>

            <div className="flex justify-center space-x-4">
              {photoModal.photo && (
                <button
                  onClick={downloadPhoto}
                  className="px-4 py-2 bg-blue-600 text-white rounded-none hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>Download</span>
                </button>
              )}
              <button
                onClick={closePhotoModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-none hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const FloatingTextArea = ({ id, label, value, onChange, required = false }) => (
  <div className="relative">
    <textarea
      id={id}
      value={value}
      onChange={onChange}
      placeholder=" "
      required={required}
      rows="3"
      className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
    ></textarea>
    <label
      htmlFor={id}
      className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
    >
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
  </div>
)

export default function EmployeePayroll() {
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(() => {
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth() + 1
    return month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`
  })
  const [availableFinancialYears] = useState(() => {
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth() + 1
    const currentFy = month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`
    const prevFy = (() => {
      const startYear = parseInt(currentFy.split("-")[0], 10) - 1
      return `${startYear}-${startYear + 1}`
    })()
    return [currentFy, prevFy]
  })
  const shouldShowIncomeTaxGuide = (() => {
    const fy = String(selectedFinancialYear || "").trim()
    const match = fy.match(/^(\d{4})-(\d{2}|\d{4})$/)
    if (!match) return false
    const startYear = Number(match[1])
    const endYearRaw = match[2]
    const endYear =
      endYearRaw.length === 2
        ? Number(`${match[1].slice(0, 2)}${endYearRaw}`)
        : Number(endYearRaw)
    return startYear === 2026 && endYear === 2027
  })()
  
  // Payroll Settings State
  const [payrollSettings, setPayrollSettings] = useState(null)
  const [loadingSettings, setLoadingSettings] = useState(false)
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null) // Store employee ID
  
  const [taxRegime, setTaxRegime] = useState("new")
  const [hasVisitedOldRegime, setHasVisitedOldRegime] = useState(false)
  const [activeTab, setActiveTab] = useState("investment-declaration")
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [savedSections, setSavedSections] = useState(new Set())
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadedDocuments, setUploadedDocuments] = useState([])
  const [docToConfirmRemoveIndex, setDocToConfirmRemoveIndex] = useState(null)
  const [declarationId, setDeclarationId] = useState(null)
  const [declarationStatus, setDeclarationStatus] = useState(null) // DRAFT, SUBMITTED, REVIEWED, REJECTED
  const [investmentReadOnly, setInvestmentReadOnly] = useState(false)
  const [employeeProfile, setEmployeeProfile] = useState(null)

  // Admin override flags from declaration header. These allow HR/admin to
  // reopen specific sections (or the whole declaration) for editing while
  // the declaration is otherwise locked by status. They never bypass
  // payroll settings/windows.
  const [allowAllEdits, setAllowAllEdits] = useState(false)
  const [allowPrevEmploymentEdit, setAllowPrevEmploymentEdit] = useState(false)
  const [allowHraEdit, setAllowHraEdit] = useState(false)
  const [allowSection80CEdit, setAllowSection80CEdit] = useState(false)
  const [allowViaEdit, setAllowViaEdit] = useState(false)
  const [allowHousePropertyEdit, setAllowHousePropertyEdit] = useState(false)

  const [fieldErrors, setFieldErrors] = useState({})

  const [submitConfirm, setSubmitConfirm] = useState({ open: false, mode: null }) // mode: 'old' | 'new'
  const [cancelConfirmMode, setCancelConfirmMode] = useState(null) // 'old_nothing_declared' | 'new' | null
  const initialTaxRegimeRef = useRef("new") // regime when view was loaded; cancel popup only when user changed regime (old↔new)

  // Track fields that had rejected documents - keep them editable until Save & Next
  const [fieldsWithRejectedDocs, setFieldsWithRejectedDocs] = useState(new Set())

  const setFieldError = (key, message) => {
    setFieldErrors((prev) => {
      const next = { ...prev }
      if (message) next[key] = message
      else delete next[key]
      return next
    })
  }

  const validateCurrentSection = (sectionId) => {
    const nextErrors = {}

    if (sectionId === "prev-employment") {
      // Validate employer name
      const employerName = (prevEmployment.employerName || '').trim()
      if (!employerName) {
        nextErrors.prevEmployerName = "Previous Employer Name is required."
      } else if (!/^[A-Za-z\s]+$/.test(employerName)) {
        nextErrors.prevEmployerName = "Previous Employer Name must contain only letters and spaces."
      }
      
      // Validate gross salary
      const grossSalary = (prevEmployment.grossSalary || '').trim()
      let e1 = getNumberRangeError("Total Salary Received (Gross)", grossSalary)
      if (!e1 && !grossSalary) {
        e1 = "Total Salary Received (Gross) is required."
      }
      if (e1) nextErrors.prevGrossSalary = e1
      
      // Validate tax deducted
      const taxDeducted = (prevEmployment.taxDeducted || '').trim()
      let e2 = getNumberRangeError("Total Tax Deducted", taxDeducted)
      if (!e2 && !taxDeducted) {
        e2 = "Total Tax Deducted is required."
      }
      // Validate that tax deducted is less than gross salary
      if (!e2 && grossSalary && taxDeducted) {
        const gross = parseFloat(grossSalary)
        const tax = parseFloat(taxDeducted)
        if (!isNaN(gross) && !isNaN(tax) && tax >= gross) {
          e2 = "Total Tax Deducted must be less than Total Salary Received (Gross)."
        }
      }
      if (e2) nextErrors.prevTaxDeducted = e2
    }

    if (sectionId === "house-rent") {
      const rows = investmentDeclaration.hraEntries || []
      for (let i = 0; i < rows.length; i++) {
        const rentKey = `rentPaid-${i}`
        const panKey = `landlordPan-${i}`
        const addrKey = `houseRentAddress-${i}`
        const rentErr = getNumberRangeError(`Total Rent Paid`, rows[i]?.totalRentPaid)
        const panErr = getPanError(`Landlord PAN`, rows[i]?.landlordPan)
        const addrErr = getAddressError(`Address`, rows[i]?.rentalAddress)
        if (rentErr) nextErrors[rentKey] = rentErr
        if (panErr) nextErrors[panKey] = panErr
        if (addrErr) nextErrors[addrKey] = addrErr

        const amount = Number(rows[i]?.totalRentPaid) || 0
        
        // If total rent > 1 lakh (100000), landlord PAN is mandatory
        if (amount > 100000) {
          const landlordPan = (rows[i]?.landlordPan || '').trim()
          if (!landlordPan) {
            nextErrors[panKey] = `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`
          } else {
            // Still validate PAN format if provided
            const panValidationErr = getPanError(`Landlord PAN`, landlordPan)
            if (panValidationErr) {
              nextErrors[panKey] = panValidationErr
            }
          }
        }

        // If rent > 0 and proofs are enabled, require proof for this HRA row
        if (
          amount > 0 &&
          proofUploadEnabled &&
          !hasProof(`HRA - Entry ${i + 1}`, "house-rent")
        ) {
          nextErrors[rentKey] =
            nextErrors[rentKey] ||
            "Proof document is required when Total Rent Paid/Payable is entered."
        }
      }
    }

    if (sectionId === "section-80c") {
      const s = investmentDeclaration.section80C || {}
      const map = [
        ["lifeInsurance", "Life Insurance Premium", s.lifeInsurance, "Life Insurance Premium"],
        ["ppf", "Public Provident Fund (PPF)", s.ppf, "Public Provident Fund (PPF)"],
        ["vpf", "Voluntary Provident Fund (VPF)", s.vpf, "Voluntary Provident Fund (VPF)"],
        ["pensionFund", "Pension Fund Contribution", s.pensionFund, "Pension Fund Contribution"],
        ["nsc", "National Savings Certificate (NSC)", s.nsc, "National Savings Certificate (NSC)"],
        ["nscInterest", "Interest Accrued on NSC (Reinvested)", s.nscInterest, "Interest Accrued on NSC (Reinvested)"],
        ["ulip", "Unit Linked Insurance Policy (ULIP)", s.ulip, "Unit Linked Insurance Policy (ULIP)"],
        ["elss", "Equity Linked Savings Schemes (ELSS)", s.elss, "Equity Linked Savings Schemes (ELSS)"],
        ["tuitionFees", "Payment of Tuition Fees (Max 2 Children)", s.tuitionFees, "Payment of Tuition Fees (Max 2 Children)"],
        ["houseLoanPrincipal", "Principal Repayment of Housing Loan", s.houseLoanPrincipal, "Principal Repayment of Housing Loan"],
        ["registrationCharges", "Registration Charges for Buying House", s.registrationCharges, "Registration Charges for Buying House"],
        ["sukanyaSamriddhi", "Sukanya Samriddhi Yojana", s.sukanyaSamriddhi, "Sukanya Samriddhi Yojana"],
        ["infrastructureBonds", "Infrastructure Bonds", s.infrastructureBonds, "Infrastructure Bonds"],
        ["fixedDeposit", "Bank Fixed Deposit (5+ Years)", s.fixedDeposit, "Bank Fixed Deposit (5+ Years)"],
      ]
      for (const [key, label, value, proofLabel] of map) {
        const err = getNumberRangeError(label, value)
        if (err) nextErrors[key] = err

        const amount = Number(value) || 0
        if (amount > 0 && proofUploadEnabled && !hasProof(proofLabel, "section-80c")) {
          nextErrors[key] =
            nextErrors[key] ||
            `Proof document is required when ${label} is entered.`
        }
      }
    }

    if (sectionId === "chapter-vi-a") {
      const d = investmentDeclaration.sectionVIA?.section80D || {}
      const o = investmentDeclaration.sectionVIA || {}
      const map = [
        ["section80DSelfNew", "Self Medical Insurance", d.self, "Section 80D - Medical Insurance"],
        ["section80DParentsNew", "Parents Medical Insurance", d.parents, "Section 80D - Medical Insurance"],
        [
          "section80DParentsSeniorNew",
          "Senior Citizen Parents Medical Insurance",
          d.parentsSenior,
          "Section 80D - Medical Insurance",
        ],
        ["preventiveHealthCheckupNew", "Preventive Health Checkup", d.preventiveHealthCheckup, "Section 80D - Medical Insurance"],
        ["section80ENew", "Interest on Education Loan (80E)", o.section80E, "Interest on Education Loan (80E)"],
        ["section80CCD1BNew", "NPS Additional Contribution (80CCD(1B))", o.section80CCD1B, "NPS Additional Contribution (80CCD(1B))"],
        ["section80EENew", "Interest on Home Loan (80EE)", o.section80EE, "Interest on Home Loan (80EE)"],
        [
          "section80EEANew",
          "Interest on Home Loan (80EEA)",
          o.section80EEA,
          "Interest on Home Loan (80EEA)",
        ],
        [
          "section80EEBNew",
          "Interest on Home Loan (80EEB)",
          o.section80EEB,
          "Interest on Home Loan (80EEB)",
        ],
        ["section80GNew", "Donations (80G)", o.section80G, "Donations (80G)"],
        ["section80TTANew", "Interest on Savings (80TTA)", o.section80TTA, "Interest on Savings Account (80TTA)"],
        ["section80TTBNew", "Interest on Deposits (80TTB)", o.section80TTB, "Interest on Deposits (80TTB)"],
      ]
      for (const [key, label, value, proofLabel] of map) {
        const err = getNumberRangeError(label, value)
        if (err) nextErrors[key] = err

        const amount = Number(value) || 0
        if (amount > 0 && proofUploadEnabled && !hasProof(proofLabel, "chapter-vi-a")) {
          nextErrors[key] =
            nextErrors[key] ||
            `Proof document is required when ${label} is entered.`
        }
      }
    }

    if (sectionId === "house-property") {
      const rows = investmentDeclaration.housePropertyEntries || []
      for (let i = 0; i < rows.length; i++) {
        const key = `interestPaidOnBorrowedCapital-${i}`
        const err = getNumberRangeError(
          `Interest Paid on Borrowed Capital`,
          rows[i]?.interestPaidOnBorrowedCapital
        )
        if (err) nextErrors[key] = err

        const amount = Number(rows[i]?.interestPaidOnBorrowedCapital) || 0
        if (
          amount > 0 &&
          proofUploadEnabled &&
          !hasProof(`House Property - Entry ${i + 1}`, "house-property")
        ) {
          nextErrors[key] =
            nextErrors[key] ||
            "Proof document is required when Interest Paid on Borrowed Capital is entered."
        }
      }
    }

    setFieldErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  // Helper functions to determine field states based on payroll settings
  const isInvestmentDeclarationEnabled = () => {
    return payrollSettings?.isInvestmentEnabled || false;
  };

  const isInvestmentProofEnabled = () => {
    return payrollSettings?.isProofEnabled || false;
  };

  const getInvestmentDeclarationMode = () => {
    if (!payrollSettings) return 'manual';
    
    const mode = payrollSettings.investmentMode;
    switch(mode) {
      case 'AUTO_MONTHLY': return 'auto';
      case 'ALWAYS': return 'always';
      case 'MANUAL': 
      default: return 'manual';
    }
  };

  const getInvestmentProofMode = () => {
    if (!payrollSettings) return 'manual';
    
    const mode = payrollSettings.proofMode;
    switch(mode) {
      case 'AUTO_YEARLY': return 'auto';
      case 'ALWAYS': return 'always';
      case 'MANUAL': 
      default: return 'manual';
    }
  };

  const isTaxRegimeLocked = () => {
    return payrollSettings?.isTaxRegimeLocked || false;
  };

  // Helper functions to check if current date is within windows
  const isWithinInvestmentWindow = () => {
    if (!payrollSettings) {
      return false;
    }
    
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-11
    const currentDay = today.getDate();
    
    if (payrollSettings.investmentMode === 'AUTO_MONTHLY') {
      // Auto mode - check if within monthly window (applies to every month)
      const fromDay = parseInt(payrollSettings.investmentFromDay) || 1;
      const toDay = parseInt(payrollSettings.investmentToDay) || 31;
      
      const result = currentDay >= fromDay && currentDay <= toDay;
      return result;
    } else {
      // Manual mode - check if before manual end date
      const endDate = payrollSettings.investmentEndDate;
      
      if (endDate) {
        const endDateTime = new Date(endDate);
        // Treat manual end date as inclusive for the entire calendar day
        endDateTime.setHours(23, 59, 59, 999);
        const result = today <= endDateTime;
        return result;
      }
      return false;
    }
  };

  const isWithinProofWindow = () => {
    if (!payrollSettings) {
      return false;
    }
    
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentDay = today.getDate();
    
    if (payrollSettings.proofMode === 'AUTO_YEARLY') {
      // Auto mode - check if within yearly window for specific month
      const proofMonth = parseInt(payrollSettings.proofMonth) || 1; // 1-12
      const fromDay = parseInt(payrollSettings.proofFromDay) || 1;
      const toDay = parseInt(payrollSettings.proofToDay) || 31;
      
      // Check if current month matches proof month
      if (currentMonth === proofMonth) {
        const result = currentDay >= fromDay && currentDay <= toDay;
        return result;
      }
      return false;
    } else {
      // Manual mode - check if before manual end date
      const endDate = payrollSettings.proofEndDate;
      
      if (endDate) {
        const endDateTime = new Date(endDate);
        // Treat manual proof end date as inclusive for the entire calendar day
        endDateTime.setHours(23, 59, 59, 999);
        const result = today <= endDateTime;
        return result;
      }
      return false;
    }
  };

  // Helper functions to determine field states

  // 1) Settings-based read-only: payroll settings are the primary source of truth
  //    for whether investment declaration is open for editing.
  const isInvestmentReadOnlyFromSettings = () => {
    // If no payroll settings and header is still in DRAFT, fall back to editable
    if (!payrollSettings) {
      return false;
    }

    // If investment declaration feature itself is disabled, everything is read-only
    if (!isInvestmentDeclarationEnabled()) {
      return true;
    }

    const mode = getInvestmentDeclarationMode();

    if (mode === "always") {
      return false; // Always editable while header is in DRAFT
    }

    if (mode === "auto") {
      // Check if current date is within the investment declaration window
      const withinWindow = isWithinInvestmentWindow();
      return !withinWindow;
    }

    // Manual mode - check if within window
    const withinWindow = isWithinInvestmentWindow();
    return !withinWindow;
  };

  // 2) Status-based lock: once declaration moves out of DRAFT, lock it regardless
  //    of the settings window. Fine-grained unlocking for specific fields is
  //    handled by isFieldEditable based on attachment status.
  const isDeclarationLockedByStatus = () => {
    if (!declarationStatus) return false;
    const statusUpper = declarationStatus.toString().toUpperCase();
    return !!(statusUpper && statusUpper !== "DRAFT");
  };

  // 3) Settings-only helper used for things that should hide when settings/windows
  //    disable investment declaration (e.g. proofUploadEnabled visibility).
  const isInvestmentDeclarationReadOnly = () => {
    return isInvestmentReadOnlyFromSettings();
  };

  // 4) Inputs read-only helper: combines settings and status. This should be used
  //    anywhere we decide whether the employee can edit inline values.
  const isInvestmentInputsReadOnly = () => {
    if (isInvestmentReadOnlyFromSettings()) return true;
    if (isDeclarationLockedByStatus()) return true;
    return false;
  };

  const isProofUploadReadOnly = () => {
    // If no payroll settings, make proof upload editable for testing
    if (!payrollSettings) {
      return false;
    }
    
    if (!isInvestmentProofEnabled()) {
      return true;
    }
    
    const mode = getInvestmentProofMode();
    
    if (mode === 'always') {
      return false; // Always editable
    }
    
    if (mode === 'auto') {
      // Check if current date is within the proof upload window
      const withinWindow = isWithinProofWindow();
      return !withinWindow;
    }
    
    // Manual mode - check if within window
    const withinWindow = isWithinProofWindow();
    return !withinWindow;
  };

  // Load payroll settings through now-accessible API
  const loadPayrollSettings = async () => {
    setLoadingSettings(true);
    try {
      // Use the now-accessible payroll settings API (authorization removed for GET)
      const response = await payrollService.getPayrollSettings();
      
      if (response.success && response.data) {
        const settings = response.data;
        setPayrollSettings(settings);
      } else {
        setPayrollSettings(null);
        console.error("Error loading employee payroll settings:", response.message);
      }
    } catch (error) {
      setPayrollSettings(null);
      // Don't show error when payroll is not available for the employee/company (404)
      if (error?.response?.status === 404) return;
      console.error("Error loading employee payroll settings:", error);
      toast.error("Error loading payroll settings (network issue). Please try again or contact admin.");
    } finally {
      setLoadingSettings(false);
    }
  };

  // Load payroll settings on component mount and when financial year changes
  useEffect(() => {
    loadPayrollSettings();
  }, [selectedFinancialYear]);
  const [investmentDeclaration, setInvestmentDeclaration] = useState({
    // Multiple HRA entries per declaration
    hraEntries: [
      {
        cityType: "metro",
        totalRentPaid: "",
        landlordPan: "",
        rentalAddress: "",
      },
    ],
    section80C: {
      lifeInsurance: "",
      ppf: "",
      vpf: "",
      pensionFund: "",
      nsc: "",
      nscInterest: "",
      ulip: "",
      elss: "",
      tuitionFees: "",
      houseLoanPrincipal: "",
      registrationCharges: "",
      sukanyaSamriddhi: "",
      infrastructureBonds: "",
      fixedDeposit: "",
    },
    sectionVIA: {
      section80D: {
        self: "",
        parents: "",
        isSelfSeniorCitizen: false,
        isParentsSeniorCitizen: false,
        preventiveHealthCheckup: "",
      },
      section80DD: "",
      section80E: "",
      section80U: "",
      section80CCD1B: "",
      section80EE: "",
      section80DDB: "",
    },
    // Multiple House Property entries per declaration
    housePropertyEntries: [
      {
        occupancy: "SELF_OCCUPIED",
        loanBeforeApr1999: false,
        interestPaidOnBorrowedCapital: "",
        propertyAddress: "",
      },
    ],
  })

  // Previous Employment header fields
  const [prevEmployment, setPrevEmployment] = useState({
    employerName: "",
    grossSalary: "",
    taxDeducted: "",
  })

  // --------------------- SECTION TOTAL HELPERS ---------------------

  const getSection80CTotal = () => {
    const s = investmentDeclaration.section80C || {}
    const keys = [
      "lifeInsurance",
      "ppf",
      "vpf",
      "pensionFund",
      "nsc",
      "nscInterest",
      "ulip",
      "elss",
      "tuitionFees",
      "houseLoanPrincipal",
      "registrationCharges",
      "sukanyaSamriddhi",
      "infrastructureBonds",
      "fixedDeposit",
    ]
    return keys.reduce((sum, key) => {
      const raw = s[key]
      const num = raw === "" || raw == null ? 0 : Number(raw) || 0
      return sum + num
    }, 0)
  }

  const getViaDeductionsTotal = () => {
    const via = investmentDeclaration.sectionVIA || {}
    const d = via.section80D || {}
    // Legacy top-level fields for some VI-A items
    const o = via || {}

    const values = [
      d.self,
      d.parents,
      d.parentsSenior,
      d.preventiveHealthCheckup,
      o.section80E,
      o.section80CCD1B,
      o.section80EE,
      o.section80EEA,
      o.section80EEB,
      o.section80G,
      o.section80TTA,
      o.section80TTB,
    ]

    return values.reduce((sum, raw) => {
      const num = raw === "" || raw == null ? 0 : Number(raw) || 0
      return sum + num
    }, 0)
  }

  const hasAnyDeclaration = () => {
    const s80c = getSection80CTotal()
    const via = getViaDeductionsTotal()
    if (s80c > 0 || via > 0) return true
    const hasHraData = (investmentDeclaration.hraEntries || []).some(
      (e) => (e.totalRentPaid != null && e.totalRentPaid !== "" && Number(e.totalRentPaid)) > 0
    )
    if (hasHraData) return true
    const hasHpData = (investmentDeclaration.housePropertyEntries || []).some(
      (e) => (e.interestPaidOnBorrowedCapital != null && e.interestPaidOnBorrowedCapital !== "" && Number(e.interestPaidOnBorrowedCapital)) > 0
    )
    if (hasHpData) return true
    return false
  }

  const handleCancelClick = () => {
    // Old cancel popup: no old declaration data (other than prev employment) and currently in old regime
    if (taxRegime === "old" && !hasAnyDeclaration()) {
      setCancelConfirmMode("old_nothing_declared")
      return
    }
    // New cancel popup: there is old regime data and currently in new regime
    if (taxRegime === "new" && hasAnyDeclaration()) {
      setCancelConfirmMode("new")
      return
    }
    // Otherwise: no popup (cancel does nothing else in employee view)
  }

  // --- HRA (multiple entries) helpers ---

  const addHraEntry = () => {
    setInvestmentDeclaration((prev) => ({
      ...prev,
      hraEntries: [
        ...prev.hraEntries,
        {
          cityType: "metro",
          totalRentPaid: "",
          landlordPan: "",
          rentalAddress: "",
        },
      ],
    }))
  }

  const removeHraEntry = (index) => {
    setInvestmentDeclaration((prev) => {
      const next = prev.hraEntries.filter((_, i) => i !== index)
      return {
        ...prev,
        hraEntries: next.length ? next : prev.hraEntries,
      }
    })
  }

  const handleHraChange = (index, field, value) => {
    const nextValue = field === "landlordPan" && typeof value === "string"
      ? value.toUpperCase()
      : value

    setInvestmentDeclaration((prev) => ({
      ...prev,
      hraEntries: prev.hraEntries.map((item, i) =>
        i === index ? { ...item, [field]: nextValue } : item
      ),
    }))

    if (field === "totalRentPaid") {
      setFieldError(`rentPaid-${index}`, getNumberRangeError(`Total Rent Paid `, value))
      
      // If total rent > 1 lakh, validate landlord PAN is present
      const amount = Number(value) || 0
      if (amount > 100000) {
        const currentRow = investmentDeclaration.hraEntries[index]
        const landlordPan = (currentRow?.landlordPan || '').trim()
        if (!landlordPan) {
          setFieldError(
            `landlordPan-${index}`,
            `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`
          )
        } else {
          // Still validate PAN format if provided
          const panValidationErr = getPanError(`Landlord PAN `, landlordPan)
          if (panValidationErr) {
            setFieldError(`landlordPan-${index}`, panValidationErr)
          }
        }
      }
    }
    if (field === "landlordPan") {
      setFieldError(
        `landlordPan-${index}`,
        getPanError(`Landlord PAN `, nextValue)
      )
      
      // Also check if rent > 1 lakh and PAN is now empty
      const currentRow = investmentDeclaration.hraEntries[index]
      const amount = Number(currentRow?.totalRentPaid) || 0
      if (amount > 100000 && !nextValue.trim()) {
        setFieldError(
          `landlordPan-${index}`,
          `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`
        )
      }
    }
    if (field === "rentalAddress") {
      setFieldError(`houseRentAddress-${index}`, getAddressError(`Address `, value))
    }
  }

  // --- House Property (multiple entries) helpers ---

  const addHousePropertyEntry = () => {
    setInvestmentDeclaration((prev) => ({
      ...prev,
      housePropertyEntries: [
        ...prev.housePropertyEntries,
        {
          occupancy: "SELF_OCCUPIED",
          loanBeforeApr1999: false,
          interestPaidOnBorrowedCapital: "",
          propertyAddress: "",
        },
      ],
    }))
  }

  const removeHousePropertyEntry = (index) => {
    setInvestmentDeclaration((prev) => {
      const next = prev.housePropertyEntries.filter((_, i) => i !== index)
      return {
        ...prev,
        housePropertyEntries: next.length ? next : prev.housePropertyEntries,
      }
    })
  }

  const handleHousePropertyChange = (index, field, value) => {
    setInvestmentDeclaration((prev) => ({
      ...prev,
      housePropertyEntries: prev.housePropertyEntries.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }))

    if (field === "interestPaidOnBorrowedCapital") {
      setFieldError(
        `interestPaidOnBorrowedCapital-${index}`,
        getNumberRangeError(
          `Interest Paid on Borrowed Capital `,
          value
        )
      )
    }
  }

  useEffect(() => {
    const loadInvestmentData = async () => {
      try {
        // Get employee ID using the same approach as EmployeeProfile
        let employeeId = null;
        
        try {
          const currentUserResponse = await authService.getCurrentUser();
          if (currentUserResponse && currentUserResponse.data) {
            const userData = currentUserResponse.data;
            employeeId = userData.employeeId; // Use employeeId field directly
          }
        } catch (error) {
          console.error("Error getting current user:", error);
        }
        
        // Fallback to localStorage if authService fails
        if (!employeeId) {
          employeeId = localStorage.getItem("employeeId");
        }
        
        if (!employeeId) {
          console.error("No employee ID found");
          return;
        }

        // Store employee ID for reuse
        setCurrentEmployeeId(employeeId);

        // Load employee profile (for experiences/isFresher) to drive Previous Employment tab visibility
        try {
          const employeeResponse = await employeeService.getEmployee(employeeId)
          if (employeeResponse && employeeResponse.data) {
            setEmployeeProfile(employeeResponse.data)
          } else {
            setEmployeeProfile(null)
          }
        } catch (e) {
          console.error("Error loading employee profile for payroll screen:", e)
          setEmployeeProfile(null)
        }

        // Use shared payrollService helper (Axios) to load normalized investment data
        let json;
        try {
          json = await payrollService.getInvestmentData(
            employeeId,
            selectedFinancialYear
          );
        } catch (err) {
          // 404 or "investment declaration not found" – payroll not available for this employee/year; don't show errors
          if (err?.response?.status === 404) return;
          const msg = err?.response?.data?.message || err?.message || "";
          if (msg.toLowerCase().includes("investment declaration not found")) return;
          console.error("Failed to load investment data", err);
          return;
        }

        // 200 with success: false and "not found" message – treat as no declaration, no error
        if (!json?.success || !json.data) {
          const msg = json?.message || "";
          if (msg.toLowerCase().includes("investment declaration not found")) return;
          return;
        }

        const { header, hra, section80c, via, houseProperty, attachments } = json.data

        // Apply Payroll Settings + status logic for inputs (now loaded from dedicated API)
        const invReadOnly = isInvestmentInputsReadOnly();
        const proofEnabled = !isProofUploadReadOnly();
        
        setInvestmentReadOnly(!!invReadOnly);
        // proofUploadEnabled is now computed dynamically, no state needed

        if (header?.status) {
          setDeclarationStatus(header.status)
        }

        // Load admin override flags from header (defaulting to false when absent)
        setAllowAllEdits(!!header?.allowAllEdits)
        setAllowPrevEmploymentEdit(!!header?.allowPrevEmploymentEdit)
        setAllowHraEdit(!!header?.allowHraEdit)
        setAllowSection80CEdit(!!header?.allowSection80CEdit)
        setAllowViaEdit(!!header?.allowViaEdit)
        setAllowHousePropertyEdit(!!header?.allowHousePropertyEdit)

        if (header?.taxRegime) {
          const regimeLower = header.taxRegime.toString().toLowerCase()
          const regime = regimeLower === "old" ? "old" : "new"
          setTaxRegime(regime)
          initialTaxRegimeRef.current = regime
        }

        if (header?.id) {
          setDeclarationId(header.id)
        }

        setPrevEmployment({
          employerName: header?.prevEmployerName?.toString() || "",
          grossSalary:
            header?.prevGrossSalary != null ? header.prevGrossSalary.toString() : "",
          taxDeducted:
            header?.prevTaxDeducted != null ? header.prevTaxDeducted.toString() : "",
        })

        setInvestmentDeclaration((prev) => {
          const mappedHra =
            Array.isArray(hra) && hra.length
              ? hra.map((row) => ({
                  cityType: row.isMetro ? "metro" : "non-metro",
                  totalRentPaid: row.totalRentPaid?.toString() ?? "",
                  landlordPan: row.landlordPan ?? "",
                  rentalAddress: row.rentalAddress ?? "",
                }))
              : [
                  {
                    cityType: "metro",
                    totalRentPaid: "",
                    landlordPan: "",
                    rentalAddress: "",
                  },
                ]

          const mappedHouseProperty =
            Array.isArray(houseProperty) && houseProperty.length
              ? houseProperty.map((row) => ({
                  occupancy: row.occupancy || "SELF_OCCUPIED",
                  loanBeforeApr1999: !!row.loanBeforeApr1999,
                  interestPaidOnBorrowedCapital:
                    row.interestPaidOnBorrowedCapital?.toString() ?? "",
                  propertyAddress: row.propertyAddress ?? "",
                }))
              : [
                  {
                    occupancy: "SELF_OCCUPIED",
                    loanBeforeApr1999: false,
                    interestPaidOnBorrowedCapital: "",
                    propertyAddress: "",
                  },
                ]

          return {
            ...prev,
            hraEntries: mappedHra,
            section80C: {
              ...prev.section80C,
              lifeInsurance:
                section80c?.lifeInsurancePremium?.toString() ?? prev.section80C.lifeInsurance,
              ppf: section80c?.ppf?.toString() ?? prev.section80C.ppf,
              vpf: section80c?.vpf?.toString() ?? prev.section80C.vpf,
              pensionFund: section80c?.pensionFund?.toString() ?? prev.section80C.pensionFund,
              nsc: section80c?.nsc?.toString() ?? prev.section80C.nsc,
              nscInterest:
                section80c?.nscInterestReinvested?.toString() ?? prev.section80C.nscInterest,
              ulip: section80c?.ulip?.toString() ?? prev.section80C.ulip,
              elss: section80c?.elss?.toString() ?? prev.section80C.elss,
              tuitionFees:
                section80c?.tuitionFees?.toString() ?? prev.section80C.tuitionFees,
              houseLoanPrincipal:
                section80c?.principalHousingLoan?.toString() ??
                prev.section80C.houseLoanPrincipal,
              registrationCharges:
                section80c?.registrationCharges?.toString() ??
                prev.section80C.registrationCharges,
              sukanyaSamriddhi:
                section80c?.sukanyaSamriddhi?.toString() ?? prev.section80C.sukanyaSamriddhi,
              infrastructureBonds:
                section80c?.infrastructureBonds?.toString() ??
                prev.section80C.infrastructureBonds,
              fixedDeposit:
                section80c?.bankFixedDeposit?.toString() ?? prev.section80C.fixedDeposit,
            },
            sectionVIA: {
              ...prev.sectionVIA,
              section80D: {
                ...prev.sectionVIA.section80D,
                self:
                  via?.selfMedicalInsurance?.toString() ?? prev.sectionVIA.section80D.self,
                parents:
                  via?.parentsMedicalInsurance?.toString() ?? prev.sectionVIA.section80D.parents,
                parentsSenior:
                  via?.seniorCitizenParentsMedical?.toString() ?? prev.sectionVIA.section80D.parentsSenior,
                preventiveHealthCheckup:
                  via?.preventiveHealthCheckup?.toString() ??
                  prev.sectionVIA.section80D.preventiveHealthCheckup,
              },
              section80E:
                via?.interestEducationLoan?.toString() ?? prev.sectionVIA.section80E,
              section80CCD1B:
                via?.npsAdditionalContribution?.toString() ??
                prev.sectionVIA.section80CCD1B,
              section80EE:
                via?.interestOnHomeLoan80EE?.toString() ?? prev.sectionVIA.section80EE,
              section80EEA:
                via?.interestOnHomeLoan80EEA?.toString() ?? prev.sectionVIA.section80EEA,
              section80EEB:
                via?.interestOnHomeLoan80EEB?.toString() ?? prev.sectionVIA.section80EEB,
              section80G: via?.donations80G?.toString() ?? prev.sectionVIA.section80G,
              section80TTA:
                via?.interestOnSavings80TTA?.toString() ?? prev.sectionVIA.section80TTA,
              section80TTB:
                via?.interestOnDeposits80TTB?.toString() ?? prev.sectionVIA.section80TTB,
            },
            housePropertyEntries: mappedHouseProperty,
          }
        })

        if (Array.isArray(attachments)) {
          const mappedDocs = attachments.map((att) => {
            const base = {
              documentName: att.sectionName || att.fieldName || "Document",
              // Use originalFileName for UI display; fall back to downloadFileName/fileName for older rows
              fileName:
                att.originalFileName || att.downloadFileName || att.fileName,
              // Expose explicit original/download names for consumers that care
              originalFileName:
                att.originalFileName || att.downloadFileName || att.fileName,
              downloadFileName: att.downloadFileName || null,
              size: att.fileSize,
              status: (att.status || "PENDING").toLowerCase(),
              lastUpdated: att.uploadedAt || att.reviewedAt || new Date().toISOString(),
              comment: att.comments || "",
              isSectionLevel: !att.fieldName,
              attachmentId: att.id,
              fileUrl: att.filePath,
            }

            const name = (att.sectionName || att.fieldName || "").toLowerCase()
            let sectionId = undefined

            if (name.includes("hra") || name.includes("house rent")) {
              sectionId = "house-rent"
            } else if (
              // VI-A deductions: make sure 80CCD/NPS/80D/80E/etc are treated as chapter-vi-a
              name.includes("80d") ||
              name.includes("section 80d") ||
              name.includes("80ccd") ||
              name.includes("nps additional") ||
              name.includes("nps") ||
              name.includes("80e") ||
              name.includes("80g") ||
              name.includes("80tta") ||
              name.includes("80ttb") ||
              name.includes("via")
            ) {
              sectionId = "chapter-vi-a"
            } else if (
              // 80C-related investments
              name.includes("80c") ||
              name.includes("life insurance") ||
              name.includes("ppf") ||
              name.includes("nsc") ||
              name.includes("elss") ||
              name.includes("pension fund") ||
              name.includes("tuition fees") ||
              name.includes("registration charges") ||
              name.includes("sukanya") ||
              name.includes("infrastructure bonds") ||
              name.includes("fixed deposit")
            ) {
              sectionId = "section-80c"
            } else if (name.includes("house property")) {
              sectionId = "house-property"
            }

            // Track fields with rejected documents
            if ((att.status || "").toString().toUpperCase() === "REJECTED") {
              const fieldKey = `${att.sectionName || att.fieldName || ""}-${sectionId || ""}`
              setFieldsWithRejectedDocs((prev) => new Set([...prev, fieldKey]))
            }

            return {
              ...base,
              sectionId,
            }
          })
          setUploadedDocuments(mappedDocs)
        }
      } catch (err) {
        // 404 = payroll/investment declaration not available for this employee – don't show errors
        if (err?.response?.status !== 404) {
          console.error("Failed to load investment data", err);
        }
      }
    }

    loadInvestmentData()
  }, [selectedFinancialYear, payrollSettings]) // Add payrollSettings dependency

  // Track if user has ever switched to old regime in this session
  useEffect(() => {
    if (taxRegime === "old" && !hasVisitedOldRegime) {
      setHasVisitedOldRegime(true)
    }
  }, [taxRegime, hasVisitedOldRegime])

  const isReadOnly = isInvestmentInputsReadOnly()
  // Proof uploads should only be available when investment declaration itself is enabled
  // and when the proof window/settings allow uploads.
  const proofUploadEnabled = !isInvestmentDeclarationReadOnly() && !isProofUploadReadOnly()

  // ---- Attachment helpers (per-line proof + editability) ----

  const hasProof = (sectionName, sectionId) => {
    const att = getFieldAttachment(sectionName, sectionId)
    return !!(att && att.attachmentId)
  }

  const getAttachmentStatus = (sectionName, sectionId) => {
    const att = getFieldAttachment(sectionName, sectionId)
    return att?.status?.toString().toUpperCase() || null
  }

  const isFieldEditable = (sectionName, sectionId) => {
    // 1) If payroll settings/windows say investments are read-only, nothing is editable
    if (isInvestmentReadOnlyFromSettings()) return false

    // 2) If declaration is not locked by status (still DRAFT), follow normal editing
    if (!isDeclarationLockedByStatus()) return true

    // 3) After submission (or any non-DRAFT status) *while settings are open*,
    //    everything is locked by default. A field can become editable again via
    //    either explicit admin overrides or rejected attachments.

    // 3a) Global override: allow all sections to be edited regardless of
    //     attachment status (still subject to payroll settings/windows).
    if (allowAllEdits) return true

    // 3b) Section-specific overrides
    if (sectionId === "prev-employment" && allowPrevEmploymentEdit) return true
    if (sectionId === "house-rent" && allowHraEdit) return true
    if (sectionId === "section-80c" && allowSection80CEdit) return true
    if (sectionId === "chapter-vi-a" && allowViaEdit) return true
    if (sectionId === "house-property" && allowHousePropertyEdit) return true

    // 3c) Fine-grained unlock: a field becomes editable again if:
    //     - its attachment was explicitly rejected (current status), OR
    //     - it previously had a rejected document (tracked in fieldsWithRejectedDocs)
    //     This ensures fields stay editable even after deleting/replacing rejected documents until Save & Next
    const status = getAttachmentStatus(sectionName, sectionId)
    if (status === "REJECTED") return true
    
    // Check if this field had a rejected document (even if now deleted/replaced)
    const fieldKey = `${sectionName || ""}-${sectionId || ""}`
    if (fieldsWithRejectedDocs.has(fieldKey)) return true
    
    return false
  }

  const ensureDeclarationId = async () => {
    if (declarationId) return declarationId

    // Get employee ID with proper fallback
    let employeeId = currentEmployeeId

    // If not in state yet, try to get it (fallback for race conditions)
    if (!employeeId) {
      try {
        const currentUserResponse = await authService.getCurrentUser()
        if (currentUserResponse && currentUserResponse.data) {
          const userData = currentUserResponse.data
          employeeId = userData.employeeId // Use employeeId field directly
          // Store it for future use
          setCurrentEmployeeId(employeeId)
        }
      } catch (error) {
        console.error("Error getting current user:", error)
      }

      // Final fallback to localStorage
      if (!employeeId) {
        employeeId = localStorage.getItem("employeeId")
      }
    }

    if (!employeeId) {
      throw new Error("Employee ID is not available for initializing declaration")
    }

    // Use shared payrollService helper to (upsert) declaration header
    const json = await payrollService.saveInvestmentData(employeeId, {
      financialYear: selectedFinancialYear,
      taxRegime: taxRegime === "old" ? "OLD" : "NEW",
    })

    if (!json?.data?.header?.id) {
      console.error("Invalid declaration init response:", json)
      throw new Error("Invalid response when initializing declaration")
    }

    const newId = json.data.header.id
    setDeclarationId(newId)
    return newId
  }

  const [attachmentPreview, setAttachmentPreview] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: "",
  })
  const incomeTaxGuideBlobUrlRef = useRef(null)
  const incomeTaxGuideDownloadUrl = `${String(API_URL || "").replace(/\/+$/, "")}/income-tax-guide/download`

  const attachmentUrlCacheRef = useRef(new Map())
  const attachmentDownloadUrlCacheRef = useRef(new Map())

  const openAttachment = async (attachmentId, fileName) => {
    if (!attachmentId) return
    try {
      const isPdf = (fileName || "").toLowerCase().endsWith(".pdf")
      const cached = attachmentUrlCacheRef.current.get(attachmentId)
      const cachedDownload = attachmentDownloadUrlCacheRef.current.get(attachmentId)
      // For PDFs, always fetch a fresh signed URL to avoid iframe rendering issues due to stale
      // presigned response headers / cached objects when the file is overwritten.
      if (cached && !isPdf) {
        setAttachmentPreview({
          isOpen: true,
          url: cached,
          downloadUrl: cachedDownload || null,
          fileName: fileName || "Document",
        })
        return
      }

      const [previewResp, downloadResp] = await Promise.all([
        getInvestmentAttachmentUrl(attachmentId),
        getInvestmentAttachmentDownloadUrl(attachmentId),
      ])
      const url = previewResp?.data?.url || previewResp?.url || previewResp
      const downloadUrl =
        downloadResp?.data?.url || downloadResp?.url || downloadResp
      if (url) {
        attachmentUrlCacheRef.current.set(attachmentId, url)
        if (downloadUrl) {
          attachmentDownloadUrlCacheRef.current.set(attachmentId, downloadUrl)
        }
        setAttachmentPreview({
          isOpen: true,
          url,
          downloadUrl: downloadUrl || null,
          fileName: fileName || "Document",
        })
      } else {
        toast.error("Unable to open document: no URL returned")
      }
    } catch (err) {
      console.error("Failed to open attachment", err)
      toast.error("Failed to open document. Please try again.")
    }
  }

  const openIncomeTaxGuide = async () => {
    try {
      const response = await api.get("/income-tax-guide", {
        responseType: "blob",
      })
      const blob = response?.data
      if (!(blob instanceof Blob)) {
        toast.error("Unable to load Income Tax Guide.")
        return
      }
      if (incomeTaxGuideBlobUrlRef.current) {
        URL.revokeObjectURL(incomeTaxGuideBlobUrlRef.current)
      }
      const blobUrl = URL.createObjectURL(blob)
      incomeTaxGuideBlobUrlRef.current = blobUrl
      setAttachmentPreview({
        isOpen: true,
        url: blobUrl,
        downloadUrl: incomeTaxGuideDownloadUrl,
        fileName: "INCOME TAX GUIDE 2026-27.pdf",
      })
    } catch (err) {
      console.error("Failed to load income tax guide preview", err)
      toast.error("Unable to open Income Tax Guide.")
    }
  }

  const handleInputChange = (section, field, value) => {
    setInvestmentDeclaration((prev) => {
      if (typeof field === "string") {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
          },
        }
      } else {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field[0]]: {
              ...prev[section][field[0]],
              [field[1]]: value,
            },
          },
        }
      }
    })

    // Inline validation for number fields
    if (section === "section80C" && typeof field === "string") {
      const labelMap = {
        lifeInsurance: "Life Insurance",
        ppf: "PPF",
        vpf: "VPF",
        pensionFund: "Pension Fund",
        nsc: "NSC",
        nscInterest: "NSC Interest",
        ulip: "ULIP",
        elss: "ELSS",
        tuitionFees: "Tuition Fees",
        houseLoanPrincipal: "House Loan Principal",
        registrationCharges: "Registration Charges",
        sukanyaSamriddhi: "Sukanya Samriddhi",
        infrastructureBonds: "Infrastructure Bonds",
        fixedDeposit: "Fixed Deposit",
      }
      if (labelMap[field]) {
        setFieldError(field, getNumberRangeError(labelMap[field], value))
      }
    }

    if (section === "sectionVIA" && Array.isArray(field) && field[0] === "section80D") {
      const labelMap = {
        self: "Self Medical Insurance",
        parents: "Parents Medical Insurance",
        preventiveHealthCheckup: "Preventive Health Checkup",
        parentsSenior: "Senior Citizen Parents Medical Insurance",
      }
      if (labelMap[field[1]]) {
        // IDs used in JSX are *New variants
        const idMap = {
          self: "section80DSelfNew",
          parents: "section80DParentsNew",
          preventiveHealthCheckup: "preventiveHealthCheckupNew",
          parentsSenior: "section80DParentsSeniorNew",
        }
        setFieldError(idMap[field[1]], getNumberRangeError(labelMap[field[1]], value))
      }
    }

    if (section === "sectionVIA" && typeof field === "string") {
      const labelMap = {
        section80E: "Interest Education Loan",
        section80CCD1B: "NPS Additional Contribution",
        section80EE: "Interest on Home Loan 80EE",
        section80EEA: "Interest on Home Loan 80EEA",
        section80EEB: "Interest on Home Loan 80EEB",
        section80G: "Donations 80G",
        section80TTA: "Interest on Savings 80TTA",
        section80TTB: "Interest on Deposits 80TTB",
      }
      const idMap = {
        section80E: "section80ENew",
        section80CCD1B: "section80CCD1BNew",
        section80EE: "section80EENew",
        section80EEA: "section80EEANew",
        section80EEB: "section80EEBNew",
        section80G: "section80GNew",
        section80TTA: "section80TTANew",
        section80TTB: "section80TTBNew",
      }
      if (labelMap[field]) {
        setFieldError(idMap[field] || field, getNumberRangeError(labelMap[field], value))
      }
    }
  }

  const handleDocumentUpload = async ({ file, sectionName, isSectionLevel }, sectionId) => {
    const effectiveDeclarationId = await ensureDeclarationId()

    // Resolve employeeId for R2 key naming (per-employee subfolders)
    let employeeId = currentEmployeeId
    if (!employeeId) {
      try {
        const currentUserResponse = await authService.getCurrentUser()
        if (currentUserResponse && currentUserResponse.data) {
          const userData = currentUserResponse.data
          employeeId = userData.employeeId
          setCurrentEmployeeId(employeeId)
        }
      } catch (err) {
        console.error("Error getting current user for proof upload:", err)
      }

      if (!employeeId) {
        employeeId = localStorage.getItem("employeeId")
      }
    }

    // Slot key: reuse sectionName plus optional sectionId index so each upload box is stable
    const slotKey = sectionId != null ? `${sectionName}-${sectionId}` : sectionName

    // Derive extension from the uploaded file name
    const dotIndex = file.name.lastIndexOf('.')
    const ext = dotIndex !== -1 ? file.name.substring(dotIndex) : ''

    // R2 storage file name should be label-based, e.g. "Life Insurance Premium.pdf"
    const storageFileName = `${sectionName}${ext}`

    const storageInfo = await uploadFileToR2(file, {
      employeeId,
      slotKey,
      storageFileName,
    })

    const attachment = await createInvestmentAttachment(effectiveDeclarationId, {
      sectionName,
      fieldName: isSectionLevel ? null : sectionName,
      r2Path: storageInfo.r2Path,
      fileUrl: storageInfo.fileUrl,
      fileName: storageInfo.fileName || file.name,
      fileSize: storageInfo.fileSize || file.size,
      originalFileName: file.name,
      // downloadFileName will be derived on the backend from employee empCode + section label
    })

    // Invalidate any cached signed URL for this attachment so preview/download always loads the latest file.
    if (attachment?.id) {
      attachmentUrlCacheRef.current.delete(attachment.id)
    }

    const docRecord = {
      documentName: sectionName,
      fileName: attachment.fileName,
      size: attachment.fileSize,
      status: (attachment.status || "PENDING").toLowerCase(),
      lastUpdated: attachment.uploadedAt || new Date().toISOString(),
      comment: attachment.comments || "",
      isSectionLevel,
      sectionId,
      attachmentId: attachment.id,
      fileUrl: attachment.filePath,
    }

    setUploadedDocuments((prev) => {
      const withoutOld = prev.filter(
        (doc) =>
          !(
            doc.documentName === sectionName &&
            (sectionId ? doc.sectionId === sectionId : true)
          )
      )
      return [...withoutOld, docRecord]
    })
    
    // If replacing a rejected document, keep the field editable
    const fieldKey = `${sectionName || ""}-${sectionId || ""}`
    if (fieldsWithRejectedDocs.has(fieldKey)) {
      // Field already tracked as having had a rejected doc, keep it editable
    } else if (docRecord.status === "rejected") {
      // New rejected document, track it
      setFieldsWithRejectedDocs((prev) => new Set([...prev, fieldKey]))
    }

    // Clear any existing "proof required" validation messages after a successful upload.
    // They will be recomputed on the next explicit validation if needed.
    setFieldErrors((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((key) => {
        const msg = next[key]
        if (
          typeof msg === "string" &&
          msg.toLowerCase().includes("proof document is required")
        ) {
          delete next[key]
        }
      })
      return next
    })

    return { attachmentId: attachment.id }
  }

  const handleRemoveAttachment = async (attachmentId) => {
    if (!attachmentId) return
    try {
      // Find the document before removing to check if it was rejected
      const docToRemove = uploadedDocuments.find((doc) => doc.attachmentId === attachmentId)
      
      await deleteInvestmentAttachment(attachmentId)
      setUploadedDocuments((prev) =>
        prev.filter((doc) => doc.attachmentId !== attachmentId)
      )
      
      // If removed document was rejected, keep field editable until Save & Next
      if (docToRemove && docToRemove.status === "rejected") {
        const fieldKey = `${docToRemove.sectionName || ""}-${docToRemove.sectionId || ""}`
        if (fieldKey) {
          setFieldsWithRejectedDocs((prev) => new Set([...prev, fieldKey]))
        }
      }
    } catch (err) {
      console.error("Failed to remove proof attachment:", err)
      toast.error("Failed to remove proof attachment. Please try again.")
    }
  }

  const renderPreviousEmployment = () => {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">Previous Employment</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FloatingInput
            id="prevEmployerName"
            label="Previous Employer Name"
            placeholder="Enter Previous Employer Name"
            value={prevEmployment.employerName}
            error={fieldErrors.prevEmployerName}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow letters and spaces
              if (value === '' || /^[A-Za-z\s]+$/.test(value)) {
                setPrevEmployment((prev) => ({
                  ...prev,
                  employerName: value,
                }));
                // Clear error when user types (validation will happen on Save & Next)
                if (fieldErrors.prevEmployerName) {
                  setFieldError("prevEmployerName", "");
                }
              }
            }}
            disabled={
              isInvestmentInputsReadOnly() &&
              !isFieldEditable("Previous Employment", "prev-employment")
            }
            required
          />

          <FloatingInput
            id="prevGrossSalary"
            label="Total Salary Received (Gross)"
            placeholder="Enter Total Salary Received (Gross)"
            type="number"
            value={prevEmployment.grossSalary}
            error={fieldErrors.prevGrossSalary}
            onChange={(e) =>
              setPrevEmployment((prev) => {
                const nextValue = e.target.value
                // Clear error when user types (validation will happen on Save & Next)
                if (fieldErrors.prevGrossSalary) {
                  setFieldError("prevGrossSalary", "")
                }
                // Clear related tax error if it was about comparison
                if (fieldErrors.prevTaxDeducted && fieldErrors.prevTaxDeducted.includes("must be less than")) {
                  setFieldError("prevTaxDeducted", "")
                }
                return {
                  ...prev,
                  grossSalary: nextValue,
                }
              })
            }
            disabled={
              isInvestmentInputsReadOnly() &&
              !isFieldEditable("Previous Employment", "prev-employment")
            }
            required
          />

          <FloatingInput
            id="prevTaxDeducted"
            label="Total Tax Deducted"
            placeholder="Enter Total Tax Deducted"
            type="number"
            value={prevEmployment.taxDeducted}
            error={fieldErrors.prevTaxDeducted}
            onChange={(e) =>
              setPrevEmployment((prev) => {
                const nextValue = e.target.value
                // Clear error when user types (validation will happen on Save & Next)
                if (fieldErrors.prevTaxDeducted) {
                  setFieldError("prevTaxDeducted", "")
                }
                return {
                  ...prev,
                  taxDeducted: nextValue,
                }
              })
            }
            disabled={
              isInvestmentInputsReadOnly() &&
              !isFieldEditable("Previous Employment", "prev-employment")
            }
            required
          />
        </div>
      </div>
    )
  }

  const hasPrevEmploymentValues =
    !!prevEmployment.employerName ||
    !!prevEmployment.grossSalary ||
    !!prevEmployment.taxDeducted

  const hasEmployeeExperiences = Array.isArray(employeeProfile?.experiences)
    ? employeeProfile.experiences.length > 0
    : false

  const isFresherRaw = employeeProfile?.isFresher
  const isNotFresher =
    isFresherRaw === false ||
    isFresherRaw === 0 ||
    isFresherRaw === "0" ||
    isFresherRaw === "false"

  const showPrevEmploymentTab =
    isNotFresher || hasEmployeeExperiences || hasPrevEmploymentValues

  const tabs = [
    ...(showPrevEmploymentTab
      ? [
          {
            id: "prev-employment",
            label: "Previous Employment",
            content: () => renderPreviousEmployment(),
          },
        ]
      : []),
    { id: "house-rent", label: "HRA", content: () => renderHouseRentDetails() },
    { id: "section-80c", label: "Section 80C", content: () => renderSection80C() },
    { id: "chapter-vi-a", label: "VIA-deductions", content: () => renderVIADeductions() },
    { id: "house-property", label: "House Property", content: () => renderHousePropertyDetails() },
  ]

  const getCurrentSection = () => tabs[currentSectionIndex]

  // Controls when the Submit Declaration button should be visible
  const shouldShowSubmitDeclaration =
    // Always available in old regime on the final section
    (taxRegime === "old" && getCurrentSection()?.id === "house-property") ||
    // In new regime, show if previous employment data is relevant/present
    (taxRegime === "new" && (showPrevEmploymentTab || hasVisitedOldRegime))

  const goToNextSection = () => {
    if (currentSectionIndex < tabs.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1)
    }
  }

  const goToPreviousSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1)
    }
  }

  const goToSection = (sectionId) => {
    const index = tabs.findIndex((tab) => tab.id === sectionId)
    if (index !== -1) {
      setCurrentSectionIndex(index)
    }
  }


  const handleSaveSection = async () => {
    const currentSection = getCurrentSection()
    if (!currentSection) return

    const sectionId = currentSection.id

    if (!validateCurrentSection(sectionId)) {
      toast.error("Please fix the highlighted fields")
      return
    }

    try {
      // Resolve employeeId (reusing the same pattern as loadInvestmentData/ensureDeclarationId)
      let employeeId = currentEmployeeId

      if (!employeeId) {
        try {
          const currentUserResponse = await authService.getCurrentUser()
          if (currentUserResponse && currentUserResponse.data) {
            const userData = currentUserResponse.data
            employeeId = userData.employeeId
            setCurrentEmployeeId(employeeId)
          }
        } catch (err) {
          console.error("Error getting current user while saving section:", err)
        }

        if (!employeeId) {
          employeeId = localStorage.getItem("employeeId")
        }
      }

      if (!employeeId) {
        toast.error("Unable to determine employee ID for saving section")
        return
      }

      // Build section-specific payload in the format backend expects
      let sectionData = {}

      // Header fields (Previous Employment)
      // Only convert to numbers if values are present (validation ensures required fields are filled)
      const headerPart = {
        prevEmployerName: prevEmployment.employerName || "",
        prevGrossSalary:
          prevEmployment.grossSalary !== "" && prevEmployment.grossSalary != null && prevEmployment.grossSalary.trim() !== ""
            ? parseFloat(prevEmployment.grossSalary)
            : null,
        prevTaxDeducted:
          prevEmployment.taxDeducted !== "" && prevEmployment.taxDeducted != null && prevEmployment.taxDeducted.trim() !== ""
            ? parseFloat(prevEmployment.taxDeducted)
            : null,
      }

      switch (sectionId) {
        case "prev-employment":
          // Previous employment is stored in declaration header fields
          sectionData = {}
          break
        case "house-rent":
          sectionData = {
            hra: (investmentDeclaration.hraEntries || []).map((entry) => ({
              isMetro: entry.cityType === "metro",
              totalRentPaid: parseFloat(entry.totalRentPaid) || 0,
              landlordPan: entry.landlordPan || "",
              rentalAddress: entry.rentalAddress || "",
            })),
          }
          break
        case "section-80c":
          sectionData = {
            section80c: {
              lifeInsurancePremium:
                parseFloat(investmentDeclaration.section80C.lifeInsurance || 0) || 0,
              ppf: parseFloat(investmentDeclaration.section80C.ppf || 0) || 0,
              vpf: parseFloat(investmentDeclaration.section80C.vpf || 0) || 0,
              pensionFund:
                parseFloat(investmentDeclaration.section80C.pensionFund || 0) || 0,
              nsc: parseFloat(investmentDeclaration.section80C.nsc || 0) || 0,
              nscInterestReinvested:
                parseFloat(investmentDeclaration.section80C.nscInterest || 0) || 0,
              ulip: parseFloat(investmentDeclaration.section80C.ulip || 0) || 0,
              elss: parseFloat(investmentDeclaration.section80C.elss || 0) || 0,
              tuitionFees:
                parseFloat(investmentDeclaration.section80C.tuitionFees || 0) || 0,
              principalHousingLoan:
                parseFloat(investmentDeclaration.section80C.houseLoanPrincipal || 0) || 0,
              registrationCharges:
                parseFloat(investmentDeclaration.section80C.registrationCharges || 0) || 0,
              sukanyaSamriddhi:
                parseFloat(investmentDeclaration.section80C.sukanyaSamriddhi || 0) || 0,
              infrastructureBonds:
                parseFloat(investmentDeclaration.section80C.infrastructureBonds || 0) || 0,
              bankFixedDeposit:
                parseFloat(investmentDeclaration.section80C.fixedDeposit || 0) || 0,
            },
          }
          break
        case "chapter-vi-a":
          sectionData = {
            via: {
              selfMedicalInsurance:
                parseFloat(investmentDeclaration.sectionVIA.section80D.self || 0) || 0,
              parentsMedicalInsurance:
                parseFloat(investmentDeclaration.sectionVIA.section80D.parents || 0) || 0,
              seniorCitizenParentsMedical:
                parseFloat(investmentDeclaration.sectionVIA.section80D.parentsSenior || 0) || 0,
              preventiveHealthCheckup:
                parseFloat(
                  investmentDeclaration.sectionVIA.section80D
                    .preventiveHealthCheckup || 0
                ) || 0,
              interestEducationLoan:
                parseFloat(investmentDeclaration.sectionVIA.section80E || 0) || 0,
              npsAdditionalContribution:
                parseFloat(investmentDeclaration.sectionVIA.section80CCD1B || 0) || 0,
              interestOnHomeLoan80EE:
                parseFloat(investmentDeclaration.sectionVIA.section80EE || 0) || 0,
              interestOnHomeLoan80EEA:
                parseFloat(investmentDeclaration.sectionVIA.section80EEA || 0) || 0,
              interestOnHomeLoan80EEB:
                parseFloat(investmentDeclaration.sectionVIA.section80EEB || 0) || 0,
              donations80G:
                parseFloat(investmentDeclaration.sectionVIA.section80G || 0) || 0,
              interestOnSavings80TTA:
                parseFloat(investmentDeclaration.sectionVIA.section80TTA || 0) || 0,
              interestOnDeposits80TTB:
                parseFloat(investmentDeclaration.sectionVIA.section80TTB || 0) || 0,
            },
          }
          break
        case "house-property":
          sectionData = {
            houseProperty: (investmentDeclaration.housePropertyEntries || []).map(
              (entry) => ({
                occupancy: entry.occupancy || "SELF_OCCUPIED",
                loanBeforeApr1999: !!entry.loanBeforeApr1999,
                interestPaidOnBorrowedCapital:
                  parseFloat(entry.interestPaidOnBorrowedCapital || 0) || 0,
                propertyAddress: entry.propertyAddress || "",
              })
            ),
          }
          break
        default:
          console.warn("Unknown section ID:", sectionId)
          return
      }

      const payload = {
        financialYear: selectedFinancialYear,
        taxRegime: taxRegime === "old" ? "OLD" : "NEW",
        // Set status to DRAFT when Save & Next (not on last section)
        status: sectionId !== "house-property" ? "DRAFT" : undefined,
        ...headerPart,
        ...sectionData,
      }

      const result = await payrollService.saveInvestmentData(employeeId, payload)

      if (!result?.success) {
        console.error("Failed to save section data:", result)
        toast.error(result?.message || `Failed to save ${currentSection.label} details`)
        return
      }

      // Mark section as saved (for checkmark in tabs)
      setSavedSections((prev) => new Set([...prev, sectionId]))
      setFieldErrors({})
      
      // Clear rejected document tracking for this section after successful save
      // This ensures fields are only editable until Save & Next is clicked
      setFieldsWithRejectedDocs((prev) => {
        const next = new Set(prev)
        // Remove all field keys that match this sectionId
        prev.forEach((key) => {
          if (key.endsWith(`-${sectionId}`)) {
            next.delete(key)
          }
        })
        return next
      })
      
      toast.success(`${currentSection.label} details saved successfully!`)

      // After save, treat current regime as "initial" so Cancel won't show popup
      initialTaxRegimeRef.current = taxRegime

      // Auto-navigate to next section after a short delay, except on last section
      if (sectionId !== "house-property") {
        setTimeout(() => {
          goToNextSection()
        }, 1000)
      }
    } catch (error) {
      console.error("Error saving section:", error)
      toast.error(`Failed to save ${currentSection.label} details: ${error.message}`)
    }
  }

  const resetCurrentSection = () => {
    const currentSection = getCurrentSection()
    if (!currentSection) return

    const sectionId = currentSection.id

    switch (sectionId) {
      case "house-rent":
        setInvestmentDeclaration((prev) => ({
          ...prev,
          houseRent: {
            cityType: "metro",
            rentPaid: "",
            periods: [],
            landlordPan: "",
            address: "",
          },
        }))
        break
      case "section-80c":
        setInvestmentDeclaration((prev) => ({
          ...prev,
          section80C: {
            lifeInsurance: "",
            ppf: "",
            vpf: "",
            pensionFund: "",
            nsc: "",
            nscInterest: "",
            ulip: "",
            elss: "",
            tuitionFees: "",
            houseLoanPrincipal: "",
            registrationCharges: "",
            sukanyaSamriddhi: "",
            infrastructureBonds: "",
            fixedDeposit: "",
          },
        }))
        break
      case "chapter-vi-a":
        setInvestmentDeclaration((prev) => ({
          ...prev,
          sectionVIA: {
            section80D: {
              self: "",
              parents: "",
              isSelfSeniorCitizen: false,
              isParentsSeniorCitizen: false,
              preventiveHealthCheckup: "",
            },
            section80DD: "",
            section80E: "",
            section80U: "",
            section80CCD1B: "",
            section80EE: "",
            section80DDB: "",
          },
        }))
        break
      case "house-property":
        setInvestmentDeclaration((prev) => ({
          ...prev,
          houseProperty: {
            propertyType: "self-occupied",
            loanTakenBeforeApril1999: false,
            interest: "",
            rentalIncome: "",
            address: "",
          },
        }))
        break
      default:
        break
    }
  }

  const handleSubmitDeclaration = () => {
    // Open confirmation modal instead of using window.confirm
    if (taxRegime === "old") {
      setSubmitConfirm({ open: true, mode: "old" })
    } else {
      setSubmitConfirm({ open: true, mode: "new" })
    }
  }

  const handleConfirmSubmit = async () => {
    try {
      const mode = submitConfirm.mode
      if (!mode) {
        setSubmitConfirm({ open: false, mode: null })
        return
      }

      // Resolve employeeId (same pattern as handleSaveSection)
      let employeeId = currentEmployeeId

      if (!employeeId) {
        try {
          const currentUserResponse = await authService.getCurrentUser()
          if (currentUserResponse && currentUserResponse.data) {
            const userData = currentUserResponse.data
            employeeId = userData.employeeId
            setCurrentEmployeeId(employeeId)
          }
        } catch (err) {
          console.error("Error getting current user while submitting declaration:", err)
        }

        if (!employeeId) {
          employeeId = localStorage.getItem("employeeId")
        }
      }

      if (!employeeId) {
        toast.error("Unable to determine employee ID for submitting declaration")
        return
      }

      // Build header payload including previous employment values
      const headerPart = {
        prevEmployerName: prevEmployment.employerName || "",
        prevGrossSalary:
          prevEmployment.grossSalary !== "" && prevEmployment.grossSalary != null
            ? parseFloat(prevEmployment.grossSalary) || 0
            : null,
        prevTaxDeducted:
          prevEmployment.taxDeducted !== "" && prevEmployment.taxDeducted != null
            ? parseFloat(prevEmployment.taxDeducted) || 0
            : null,
      }

      if (mode === "old") {
        const payload = {
          financialYear: selectedFinancialYear,
          taxRegime: "OLD",
          status: "SUBMITTED",
          ...headerPart,
        }

        const result = await payrollService.saveInvestmentData(employeeId, payload)
        if (!result?.success) {
          console.error("Failed to submit declaration (old regime):", result)
          toast.error(result?.message || "Failed to submit declaration")
          return
        }
        setDeclarationStatus("SUBMITTED")
        setInvestmentReadOnly(true)
        initialTaxRegimeRef.current = "old"
        toast.success("Declaration submitted successfully under Old Tax Regime")
      } else {
        // New regime submission flow
        try {
          // Clear any existing investment data for this employee/year (sections + attachments)
          await payrollService.deleteInvestmentData(employeeId, selectedFinancialYear)
        } catch (err) {
          console.error(
            "Failed to clear old regime investment data before submitting new regime:",
            err
          )

          // If there is simply no existing declaration to delete (404), continue.
          // For other errors (permission, server error), block submission.
          const status = err?.response?.status
          if (status && status !== 404) {
            toast.error("Failed to clear existing investment data. Please try again.")
            return
          }
        }

        const payload = {
          financialYear: selectedFinancialYear,
          taxRegime: "NEW",
          status: "SUBMITTED",
          ...headerPart,
        }

        const result = await payrollService.saveInvestmentData(employeeId, payload)
        if (!result?.success) {
          console.error("Failed to submit declaration (new regime):", result)
          toast.error(result?.message || "Failed to submit declaration")
          return
        }
        setDeclarationStatus("SUBMITTED")
        setInvestmentReadOnly(true)
        initialTaxRegimeRef.current = "new"
        toast.success("Declaration submitted successfully under New Tax Regime")
      }
    } catch (error) {
      console.error("Error submitting declaration:", error)
      toast.error(error?.message || "Error submitting declaration")
    } finally {
      setSubmitConfirm({ open: false, mode: null })
    }
  }

  const getFieldAttachment = (sectionName, sectionId) => {
    const normalize = (name) => {
      if (!name) return ""
      let n = name.toString().toLowerCase().trim()
      // Backwards-compatibility: older data may use longer HRA label
      n = n.replace("house rent allowance (hra) - ", "hra - ")
      // Normalize various 80D labels to a single key
      n = n.replace("self medical insurance (80d)", "section 80d - medical insurance")
      n = n.replace("section 80d", "section 80d - medical insurance")
      // Normalize NPS 80CCD(1B) label variants
      n = n.replace("section 80ccd(1b)", "nps additional contribution (80ccd(1b))")
      // Normalize 80TTA variations
      n = n.replace("interest on savings (80tta)", "interest on savings account (80tta)")
      n = n.replace("interest on savings account (80tta)", "interest on savings account (80tta)")
      // Normalize 80TTB variations
      n = n.replace("interest on deposits (80ttb)", "interest on deposits (80ttb)")
      return n
    }

    const target = normalize(sectionName)

    return uploadedDocuments.find((doc) => {
      // If both sides have a sectionId and they differ, skip.
      if (sectionId && doc.sectionId && doc.sectionId !== sectionId) return false
      return normalize(doc.documentName) === target
    })
  }

  const renderHouseRentDetails = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-2">House Rent Allowance (HRA)</h3>

      {investmentDeclaration.hraEntries.map((entry, index) => (
        <div key={index} className="p-4 bg-gray-50 rounded-lg mb-4">
          <div className="space-y-2 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="flex space-x-10">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`cityType-${index}`}
                  value="metro"
                  checked={entry.cityType === "metro"}
                  disabled={
                    isInvestmentInputsReadOnly() &&
                    !isFieldEditable(`HRA - Entry ${index + 1}`, "house-rent")
                  }
                  onChange={(e) => handleHraChange(index, "cityType", e.target.value)}
                  className="mr-2"
                />
                Metro
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`cityType-${index}`}
                  value="non-metro"
                  checked={entry.cityType === "non-metro"}
                  disabled={
                    isInvestmentInputsReadOnly() &&
                    !isFieldEditable(`HRA - Entry ${index + 1}`, "house-rent")
                  }
                  onChange={(e) => handleHraChange(index, "cityType", e.target.value)}
                  className="mr-2"
                />
                Non-Metro
              </label>
            </div>
            <FloatingInput
              id={`rentPaid-${index}`}
              label="Total Rent Paid/Payable"
              placeholder="Total Rent Paid/Payable"
              type="number"
              value={entry.totalRentPaid}
              // Total Rent Paid/Payable is the only HRA input that re-opens after
              // submit when its proof is rejected.
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable(`HRA - Entry ${index + 1}`, "house-rent")}
              onChange={(e) => handleHraChange(index, "totalRentPaid", e.target.value)}
              error={fieldErrors[`rentPaid-${index}`]}
            />
            <FloatingInput
              id={`landlordPan-${index}`}
              label="Landlord PAN"
              placeholder="Landlord PAN"
              value={entry.landlordPan}
              disabled={
                isInvestmentInputsReadOnly() &&
                !isFieldEditable(`HRA - Entry ${index + 1}`, "house-rent")
              }
              onChange={(e) => handleHraChange(index, "landlordPan", e.target.value)}
              error={fieldErrors[`landlordPan-${index}`]}
            />
            <FloatingInput
              id={`houseRentAddress-${index}`}
              label="Address"
              placeholder="Address"
              value={entry.rentalAddress}
              disabled={
                isInvestmentInputsReadOnly() &&
                !isFieldEditable(`HRA - Entry ${index + 1}`, "house-rent")
              }
              onChange={(e) => handleHraChange(index, "rentalAddress", e.target.value)}
              error={fieldErrors[`houseRentAddress-${index}`]}
            />
          </div>

          <div className="mt-4">
            {proofUploadEnabled && (
            <SectionUpload
              sectionName={`HRA - Entry ${index + 1}`}
              onUpload={(data) => handleDocumentUpload(data, "house-rent")}
              onRemove={deleteInvestmentAttachment}
              // HRA upload is the only proof widget that re-opens after submit
              // for this row when its proof is rejected.
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable(`HRA - Entry ${index + 1}`, "house-rent")}
              isSectionLevel={true}
              initialAttachmentId={
                getFieldAttachment(
                  `HRA - Entry ${index + 1}`,
                  "house-rent"
                )?.attachmentId
              }
              initialFileName={
                getFieldAttachment(
                  `HRA - Entry ${index + 1}`,
                  "house-rent"
                )?.fileName
              }
              initialFileSize={
                getFieldAttachment(
                  `HRA - Entry ${index + 1}`,
                  "house-rent"
                )?.size
              }
              initialStatus={
                getFieldAttachment(
                  `HRA - Entry ${index + 1}`,
                  "house-rent"
                )?.status || "pending"
              }
              initialRejectReason={
                getFieldAttachment(
                  `HRA - Entry ${index + 1}`,
                  "house-rent"
                )?.comment || ""
              }
              onViewAttachment={openAttachment}
            />
            )}
          </div>

          {!isInvestmentInputsReadOnly() && index > 0 && (
            <button
              type="button"
              className="mt-2 text-red-600 hover:text-red-800 text-sm"
              onClick={() => removeHraEntry(index)}
            >
              Remove HRA
            </button>
          )}
        </div>
      ))}

      {/* {!isReadOnly && (
        <button
          type="button"
          onClick={addHraEntry}
          className="m-2 text-blue-600 hover:text-blue-800 text-sm"
        >
          + Add HRA
        </button>
      )} */}
    </div>
  )

  const renderSection80C = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Section 80C Deductions</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <FloatingInput
            id="lifeInsurance"
            label="Life Insurance Premium"
            placeholder="Life Insurance Premium"
            type="number"
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Life Insurance Premium", "section-80c")}
            value={investmentDeclaration.section80C.lifeInsurance}
            onChange={(e) => handleInputChange("section80C", "lifeInsurance", e.target.value)}
            error={fieldErrors.lifeInsurance}
          />
          {proofUploadEnabled && (
          <SectionUpload
            sectionName="Life Insurance Premium"
            onUpload={(data) => handleDocumentUpload(data, "section-80c")}
            onRemove={handleRemoveAttachment}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Life Insurance Premium", "section-80c")}
            initialAttachmentId={getFieldAttachment("Life Insurance Premium", "section-80c")?.attachmentId}
            initialFileName={getFieldAttachment("Life Insurance Premium", "section-80c")?.fileName}
            initialFileSize={getFieldAttachment("Life Insurance Premium", "section-80c")?.size}
            initialStatus={getFieldAttachment("Life Insurance Premium", "section-80c")?.status || "pending"}
            initialRejectReason={getFieldAttachment("Life Insurance Premium", "section-80c")?.comment || ""}
            onViewAttachment={openAttachment}
          />
          )}
        </div>
        <div>
          <FloatingInput
            id="ppf"
            label="Public Provident Fund (PPF)"
            placeholder="Public Provident Fund (PPF)"
            type="number"
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Public Provident Fund (PPF)", "section-80c")}
            value={investmentDeclaration.section80C.ppf}
            onChange={(e) => handleInputChange("section80C", "ppf", e.target.value)}
            error={fieldErrors.ppf}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Public Provident Fund (PPF)"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Public Provident Fund (PPF)", "section-80c")}
              initialAttachmentId={getFieldAttachment("Public Provident Fund (PPF)", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Public Provident Fund (PPF)", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Public Provident Fund (PPF)", "section-80c")?.size}
              initialStatus={getFieldAttachment("Public Provident Fund (PPF)", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Public Provident Fund (PPF)", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="vpf"
            label="Voluntary Provident Fund (VPF)"
            placeholder="Voluntary Provident Fund (VPF)"
            type="number"
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Voluntary Provident Fund (VPF)", "section-80c")}
            value={investmentDeclaration.section80C.vpf}
            onChange={(e) => handleInputChange("section80C", "vpf", e.target.value)}
            error={fieldErrors.vpf}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Voluntary Provident Fund (VPF)"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Voluntary Provident Fund (VPF)", "section-80c")}
              initialAttachmentId={getFieldAttachment("Voluntary Provident Fund (VPF)", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Voluntary Provident Fund (VPF)", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Voluntary Provident Fund (VPF)", "section-80c")?.size}
              initialStatus={getFieldAttachment("Voluntary Provident Fund (VPF)", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Voluntary Provident Fund (VPF)", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="pensionFund"
            label="Pension Fund Contribution"
            placeholder="Pension Fund Contribution"
            type="number"
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Pension Fund Contribution", "section-80c")}
            value={investmentDeclaration.section80C.pensionFund}
            onChange={(e) => handleInputChange("section80C", "pensionFund", e.target.value)}
            error={fieldErrors.pensionFund}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Pension Fund Contribution"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Pension Fund Contribution", "section-80c")}
              initialAttachmentId={getFieldAttachment("Pension Fund Contribution", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Pension Fund Contribution", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Pension Fund Contribution", "section-80c")?.size}
              initialStatus={getFieldAttachment("Pension Fund Contribution", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Pension Fund Contribution", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="nsc"
            label="National Savings Certificate (NSC)"
            placeholder="National Savings Certificate (NSC)"
            type="number"
            value={investmentDeclaration.section80C.nsc}
            onChange={(e) => handleInputChange("section80C", "nsc", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("National Savings Certificate (NSC)", "section-80c")}
            error={fieldErrors.nsc}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="National Savings Certificate (NSC)"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("National Savings Certificate (NSC)", "section-80c")}
              initialAttachmentId={getFieldAttachment("National Savings Certificate (NSC)", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("National Savings Certificate (NSC)", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("National Savings Certificate (NSC)", "section-80c")?.size}
              initialStatus={getFieldAttachment("National Savings Certificate (NSC)", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("National Savings Certificate (NSC)", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="nscInterest"
            label="Interest Accrued on NSC (Reinvested)"
            placeholder="Interest Accrued on NSC (Reinvested)"
            type="number"
            value={investmentDeclaration.section80C.nscInterest}
            onChange={(e) => handleInputChange("section80C", "nscInterest", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest Accrued on NSC (Reinvested)", "section-80c")}
            error={fieldErrors.nscInterest}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Interest Accrued on NSC (Reinvested)"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest Accrued on NSC (Reinvested)", "section-80c")}
              initialAttachmentId={getFieldAttachment("Interest Accrued on NSC (Reinvested)", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Interest Accrued on NSC (Reinvested)", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Interest Accrued on NSC (Reinvested)", "section-80c")?.size}
              initialStatus={getFieldAttachment("Interest Accrued on NSC (Reinvested)", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Interest Accrued on NSC (Reinvested)", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="ulip"
            label="Unit Linked Insurance Policy (ULIP)"
            placeholder="Unit Linked Insurance Policy (ULIP)"
            type="number"
            value={investmentDeclaration.section80C.ulip}
            onChange={(e) => handleInputChange("section80C", "ulip", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Unit Linked Insurance Policy (ULIP)", "section-80c")}
            error={fieldErrors.ulip}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Unit Linked Insurance Policy (ULIP)"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Unit Linked Insurance Policy (ULIP)", "section-80c")}
              initialAttachmentId={getFieldAttachment("Unit Linked Insurance Policy (ULIP)", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Unit Linked Insurance Policy (ULIP)", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Unit Linked Insurance Policy (ULIP)", "section-80c")?.size}
              initialStatus={getFieldAttachment("Unit Linked Insurance Policy (ULIP)", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Unit Linked Insurance Policy (ULIP)", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="elss"
            label="Equity Linked Savings Schemes (ELSS)"
            placeholder="Equity Linked Savings Schemes (ELSS)"
            type="number"
            value={investmentDeclaration.section80C.elss}
            onChange={(e) => handleInputChange("section80C", "elss", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Equity Linked Savings Schemes (ELSS)", "section-80c")}
            error={fieldErrors.elss}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Equity Linked Savings Schemes (ELSS)"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Equity Linked Savings Schemes (ELSS)", "section-80c")}
              initialAttachmentId={getFieldAttachment("Equity Linked Savings Schemes (ELSS)", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Equity Linked Savings Schemes (ELSS)", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Equity Linked Savings Schemes (ELSS)", "section-80c")?.size}
              initialStatus={getFieldAttachment("Equity Linked Savings Schemes (ELSS)", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Equity Linked Savings Schemes (ELSS)", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="tuitionFees"
            label="Payment of Tuition Fees (Max 2 Children)"
            placeholder="Payment of Tuition Fees (Max 2 Children)"
            type="number"
            value={investmentDeclaration.section80C.tuitionFees}
            onChange={(e) => handleInputChange("section80C", "tuitionFees", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Payment of Tuition Fees (Max 2 Children)", "section-80c")}
            error={fieldErrors.tuitionFees}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Payment of Tuition Fees (Max 2 Children)"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Payment of Tuition Fees (Max 2 Children)", "section-80c")}
              initialAttachmentId={getFieldAttachment("Payment of Tuition Fees (Max 2 Children)", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Payment of Tuition Fees (Max 2 Children)", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Payment of Tuition Fees (Max 2 Children)", "section-80c")?.size}
              initialStatus={getFieldAttachment("Payment of Tuition Fees (Max 2 Children)", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Payment of Tuition Fees (Max 2 Children)", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="houseLoanPrincipal"
            label="Principal Repayment of Housing Loan"
            placeholder="Principal Repayment of Housing Loan"
            type="number"
            value={investmentDeclaration.section80C.houseLoanPrincipal}
            onChange={(e) => handleInputChange("section80C", "houseLoanPrincipal", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Principal Repayment of Housing Loan", "section-80c")}
            error={fieldErrors.houseLoanPrincipal}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Principal Repayment of Housing Loan"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Principal Repayment of Housing Loan", "section-80c")}
              initialAttachmentId={getFieldAttachment("Principal Repayment of Housing Loan", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Principal Repayment of Housing Loan", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Principal Repayment of Housing Loan", "section-80c")?.size}
              initialStatus={getFieldAttachment("Principal Repayment of Housing Loan", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Principal Repayment of Housing Loan", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="registrationCharges"
            label="Registration Charges for Buying House (1 Year Only)"
            placeholder="Registration Charges for Buying House (1 Year Only)"
            type="number"
            value={investmentDeclaration.section80C.registrationCharges}
            onChange={(e) => handleInputChange("section80C", "registrationCharges", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Registration Charges for Buying House", "section-80c")}
            error={fieldErrors.registrationCharges}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Registration Charges for Buying House"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Registration Charges for Buying House", "section-80c")}
              initialAttachmentId={getFieldAttachment("Registration Charges for Buying House", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Registration Charges for Buying House", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Registration Charges for Buying House", "section-80c")?.size}
              initialStatus={getFieldAttachment("Registration Charges for Buying House", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Registration Charges for Buying House", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="sukanyaSamriddhi"
            label="Sukanya Samriddhi Yojana"
            placeholder="Sukanya Samriddhi Yojana"
            type="number"
            value={investmentDeclaration.section80C.sukanyaSamriddhi}
            onChange={(e) => handleInputChange("section80C", "sukanyaSamriddhi", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Sukanya Samriddhi Yojana", "section-80c")}
            error={fieldErrors.sukanyaSamriddhi}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Sukanya Samriddhi Yojana"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Sukanya Samriddhi Yojana", "section-80c")}
              initialAttachmentId={getFieldAttachment("Sukanya Samriddhi Yojana", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Sukanya Samriddhi Yojana", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Sukanya Samriddhi Yojana", "section-80c")?.size}
              initialStatus={getFieldAttachment("Sukanya Samriddhi Yojana", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Sukanya Samriddhi Yojana", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="infrastructureBonds"
            label="Infrastructure Bonds"
            placeholder="Infrastructure Bonds"
            type="number"
            value={investmentDeclaration.section80C.infrastructureBonds}
            onChange={(e) => handleInputChange("section80C", "infrastructureBonds", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Infrastructure Bonds", "section-80c")}
            error={fieldErrors.infrastructureBonds}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Infrastructure Bonds"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Infrastructure Bonds", "section-80c")}
              initialAttachmentId={getFieldAttachment("Infrastructure Bonds", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Infrastructure Bonds", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Infrastructure Bonds", "section-80c")?.size}
              initialStatus={getFieldAttachment("Infrastructure Bonds", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Infrastructure Bonds", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
        <div>
          <FloatingInput
            id="fixedDeposit"
            label="Bank Fixed Deposit (5+ Years)"
            placeholder="Bank Fixed Deposit (5+ Years)"
            type="number"
            value={investmentDeclaration.section80C.fixedDeposit}
            onChange={(e) => handleInputChange("section80C", "fixedDeposit", e.target.value)}
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Bank Fixed Deposit (5+ Years)", "section-80c")}
            error={fieldErrors.fixedDeposit}
          />
          {proofUploadEnabled && (
            <SectionUpload
              sectionName="Bank Fixed Deposit (5+ Years)"
              onUpload={(data) => handleDocumentUpload(data, "section-80c")}
              onRemove={handleRemoveAttachment}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Bank Fixed Deposit (5+ Years)", "section-80c")}
              initialAttachmentId={getFieldAttachment("Bank Fixed Deposit (5+ Years)", "section-80c")?.attachmentId}
              initialFileName={getFieldAttachment("Bank Fixed Deposit (5+ Years)", "section-80c")?.fileName}
              initialFileSize={getFieldAttachment("Bank Fixed Deposit (5+ Years)", "section-80c")?.size}
              initialStatus={getFieldAttachment("Bank Fixed Deposit (5+ Years)", "section-80c")?.status || "pending"}
              initialRejectReason={getFieldAttachment("Bank Fixed Deposit (5+ Years)", "section-80c")?.comment || ""}
              onViewAttachment={openAttachment}
            />
          )}
        </div>
      </div>
    </div>
  )


    // 4. VIA-deductions (Combined Section 80D and Other Investments)
  const renderVIADeductions = () => (
    <div className="space-y-6">
      {/* Section 80D - Medical Insurance */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">Section 80D - Medical Insurance</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FloatingInput
            id="section80DSelfNew"
            label="Self Medical Insurance (80D)"
            placeholder="Self Medical Insurance (80D)"
            type="number"
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Section 80D - Medical Insurance", "chapter-vi-a")}
            value={investmentDeclaration.sectionVIA.section80D.self}
            onChange={(e) => handleInputChange("sectionVIA", ["section80D", "self"], e.target.value)}
            error={fieldErrors.section80DSelfNew}
          />
          <FloatingInput
            id="section80DParentsNew"
            label="Parents Medical Insurance (80D)"
            placeholder="Parents Medical Insurance (80D)"
            type="number"
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Section 80D - Medical Insurance", "chapter-vi-a")}
            value={investmentDeclaration.sectionVIA.section80D.parents}
            onChange={(e) => handleInputChange("sectionVIA", ["section80D", "parents"], e.target.value)}
            error={fieldErrors.section80DParentsNew}
          />
          <FloatingInput
            id="section80DParentsSeniorNew"
            label="Senior Citizen Parents Medical Insurance (80D)"
            placeholder="Senior Citizen Parents Medical Insurance (80D)"
            type="number"
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Section 80D - Medical Insurance", "chapter-vi-a")}
            value={investmentDeclaration.sectionVIA.section80D.parentsSenior || ""}
            onChange={(e) => handleInputChange("sectionVIA", ["section80D", "parentsSenior"], e.target.value)}
            error={fieldErrors.section80DParentsSeniorNew}
          />
          <FloatingInput
            id="preventiveHealthCheckupNew"
            label="Preventive Health Checkup (80D)"
            placeholder="Preventive Health Checkup (80D)"
            type="number"
            disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Section 80D - Medical Insurance", "chapter-vi-a")}
            value={investmentDeclaration.sectionVIA.section80D.preventiveHealthCheckup}
            onChange={(e) =>
              handleInputChange("sectionVIA", ["section80D", "preventiveHealthCheckup"], e.target.value)
            }
            error={fieldErrors.preventiveHealthCheckupNew}
          />
        </div>

        {/* Section-level upload for Section 80D */}
        {taxRegime === "old" && (
          <div className="mt-4">
            {proofUploadEnabled && (
              <SectionUpload
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Section 80D - Medical Insurance", "chapter-vi-a")}
                sectionName="Section 80D - Medical Insurance"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                isSectionLevel={true}
                initialAttachmentId={
                  getFieldAttachment("Section 80D - Medical Insurance", "chapter-vi-a")?.attachmentId
                }
                initialFileName={
                  getFieldAttachment("Section 80D - Medical Insurance", "chapter-vi-a")?.fileName
                }
                initialFileSize={
                  getFieldAttachment("Section 80D - Medical Insurance", "chapter-vi-a")?.size
                }
                initialStatus={getFieldAttachment("Section 80D - Medical Insurance", "chapter-vi-a")?.status || "pending"}
                initialRejectReason={getFieldAttachment("Section 80D - Medical Insurance", "chapter-vi-a")?.comment || ""}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
        )}
      </div>

      {/* Other Investments */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">Other Investments</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <FloatingInput
              id="section80ENew"
              label="Interest on Education Loan (80E)"
              placeholder="Interest on Education Loan (80E)"
              type="number"
              value={investmentDeclaration.sectionVIA.section80E}
              onChange={(e) => handleInputChange("sectionVIA", "section80E", e.target.value)}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Education Loan (80E)", "chapter-vi-a")}
              error={fieldErrors.section80ENew}
            />
            {taxRegime === "old" && proofUploadEnabled && (
              <SectionUpload
                sectionName="Interest on Education Loan (80E)"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Education Loan (80E)", "chapter-vi-a")}
                initialAttachmentId={
                  getFieldAttachment(
                    "Interest on Education Loan (80E)",
                    "chapter-vi-a"
                  )?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(
                    "Interest on Education Loan (80E)",
                    "chapter-vi-a"
                  )?.fileName
                }
                initialFileSize={
                  getFieldAttachment(
                    "Interest on Education Loan (80E)",
                    "chapter-vi-a"
                  )?.size
                }
                initialStatus={getFieldAttachment("Interest on Education Loan (80E)","chapter-vi-a")?.status || "pending"}
                initialRejectReason={getFieldAttachment("Interest on Education Loan (80E)","chapter-vi-a")?.comment || ""}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
            <FloatingInput
              id="section80CCD1BNew"
              label="NPS Additional Contribution (80CCD(1B))"
              placeholder="NPS Additional Contribution (80CCD(1B))"
              type="number"
              value={investmentDeclaration.sectionVIA.section80CCD1B}
              onChange={(e) => handleInputChange("sectionVIA", "section80CCD1B", e.target.value)}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("NPS Additional Contribution (80CCD(1B))", "chapter-vi-a")}
              error={fieldErrors.section80CCD1BNew}
            />
            {taxRegime === "old" && proofUploadEnabled && (
              <SectionUpload
                sectionName="NPS Additional Contribution (80CCD(1B))"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("NPS Additional Contribution (80CCD(1B))", "chapter-vi-a")}
                initialAttachmentId={
                  getFieldAttachment(
                    "NPS Additional Contribution (80CCD(1B))",
                    "chapter-vi-a"
                  )?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(
                    "NPS Additional Contribution (80CCD(1B))",
                    "chapter-vi-a"
                  )?.fileName
                }
                initialFileSize={
                  getFieldAttachment(
                    "NPS Additional Contribution (80CCD(1B))",
                    "chapter-vi-a"
                  )?.size
                }
                initialStatus={getFieldAttachment("NPS Additional Contribution (80CCD(1B))","chapter-vi-a")?.status || "pending"}
                initialRejectReason={getFieldAttachment("NPS Additional Contribution (80CCD(1B))","chapter-vi-a")?.comment || ""}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
            <FloatingInput
              id="section80EENew"
              label="Interest on Home Loan (80EE)"
              placeholder="Interest on Home Loan (80EE)"
              type="number"
              value={investmentDeclaration.sectionVIA.section80EE}
              onChange={(e) => handleInputChange("sectionVIA", "section80EE", e.target.value)}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Home Loan (80EE)", "chapter-vi-a")}
              error={fieldErrors.section80EENew}
            />
            {taxRegime === "old" && proofUploadEnabled && (
              <SectionUpload
                sectionName="Interest on Home Loan (80EE)"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Home Loan (80EE)", "chapter-vi-a")}
                initialAttachmentId={
                  getFieldAttachment(
                    "Interest on Home Loan (80EE)",
                    "chapter-vi-a"
                  )?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(
                    "Interest on Home Loan (80EE)",
                    "chapter-vi-a"
                  )?.fileName
                }
                initialFileSize={
                  getFieldAttachment(
                    "Interest on Home Loan (80EE)",
                    "chapter-vi-a"
                  )?.size
                }
                initialStatus={getFieldAttachment("Interest on Home Loan (80EE)","chapter-vi-a")?.status || "pending"}
                initialRejectReason={getFieldAttachment("Interest on Home Loan (80EE)","chapter-vi-a")?.comment || ""}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
            <FloatingInput
              id="section80EEANew"
              label="Interest on Home Loan (80EEA)"
              placeholder="Interest on Home Loan (80EEA)"
              type="number"
              value={investmentDeclaration.sectionVIA.section80EEA || ""}
              onChange={(e) => handleInputChange("sectionVIA", "section80EEA", e.target.value)}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Home Loan (80EEA)", "chapter-vi-a")}
              error={fieldErrors.section80EEANew}
            />
            {taxRegime === "old" && proofUploadEnabled && (
              <SectionUpload
                sectionName="Interest on Home Loan (80EEA)"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Home Loan (80EEA)", "chapter-vi-a")}
                initialAttachmentId={
                  getFieldAttachment(
                    "Interest on Home Loan (80EEA)",
                    "chapter-vi-a"
                  )?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(
                    "Interest on Home Loan (80EEA)",
                    "chapter-vi-a"
                  )?.fileName
                }
                initialFileSize={
                  getFieldAttachment(
                    "Interest on Home Loan (80EEA)",
                    "chapter-vi-a"
                  )?.size
                }
                initialStatus={getFieldAttachment("Interest on Home Loan (80EEA)","chapter-vi-a")?.status || "pending"}
                initialRejectReason={getFieldAttachment("Interest on Home Loan (80EEA)","chapter-vi-a")?.comment || ""}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
            <FloatingInput
              id="section80EEBNew"
              label="Interest on Home Loan (80EEB)"
              placeholder="Interest on Home Loan (80EEB)"
              type="number"
              value={investmentDeclaration.sectionVIA.section80EEB || ""}
              onChange={(e) => handleInputChange("sectionVIA", "section80EEB", e.target.value)}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Home Loan (80EEB)", "chapter-vi-a")}
              error={fieldErrors.section80EEBNew}
            />
            {taxRegime === "old" && proofUploadEnabled && (
              <SectionUpload
                sectionName="Interest on Home Loan (80EEB)"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Home Loan (80EEB)", "chapter-vi-a")}
                initialAttachmentId={
                  getFieldAttachment(
                    "Interest on Home Loan (80EEB)",
                    "chapter-vi-a"
                  )?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(
                    "Interest on Home Loan (80EEB)",
                    "chapter-vi-a"
                  )?.fileName
                }
                initialFileSize={
                  getFieldAttachment(
                    "Interest on Home Loan (80EEB)",
                    "chapter-vi-a"
                  )?.size
                }
                initialStatus={getFieldAttachment("Interest on Home Loan (80EEB)","chapter-vi-a")?.status || "pending"}
                initialRejectReason={getFieldAttachment("Interest on Home Loan (80EEB)","chapter-vi-a")?.comment || ""}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
            <FloatingInput
              id="section80GNew"
              label="Donations (80G)"
              placeholder="Donations (80G)"
              type="number"
              value={investmentDeclaration.sectionVIA.section80G || ""}
              onChange={(e) => handleInputChange("sectionVIA", "section80G", e.target.value)}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Donations (80G)", "chapter-vi-a")}
              error={fieldErrors.section80GNew}
            />
            {taxRegime === "old" && proofUploadEnabled && (
              <SectionUpload
                sectionName="Donations (80G)"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Donations (80G)", "chapter-vi-a")}
                initialAttachmentId={
                  getFieldAttachment(
                    "Donations (80G)",
                    "chapter-vi-a"
                  )?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(
                    "Donations (80G)",
                    "chapter-vi-a"
                  )?.fileName
                }
                initialFileSize={
                  getFieldAttachment(
                    "Donations (80G)",
                    "chapter-vi-a"
                  )?.size
                }
                initialStatus={getFieldAttachment("Donations (80G)","chapter-vi-a")?.status || "pending"}
                initialRejectReason={getFieldAttachment("Donations (80G)","chapter-vi-a")?.comment || ""}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
            <FloatingInput
              id="section80TTANew"
              label="Interest on Savings Account (80TTA)"
              placeholder="Interest on Savings Account (80TTA)"
              type="number"
              value={investmentDeclaration.sectionVIA.section80TTA || ""}
              onChange={(e) => handleInputChange("sectionVIA", "section80TTA", e.target.value)}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Savings Account (80TTA)", "chapter-vi-a")}
              error={fieldErrors.section80TTANew}
            />
            {taxRegime === "old" && proofUploadEnabled && (
              <SectionUpload
                sectionName="Interest on Savings Account (80TTA)"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Savings Account (80TTA)", "chapter-vi-a")}
                initialAttachmentId={
                  getFieldAttachment(
                    "Interest on Savings Account (80TTA)",
                    "chapter-vi-a"
                  )?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(
                    "Interest on Savings Account (80TTA)",
                    "chapter-vi-a"
                  )?.fileName
                }
                initialFileSize={
                  getFieldAttachment(
                    "Interest on Savings Account (80TTA)",
                    "chapter-vi-a"
                  )?.size
                }
                initialRejectReason={getFieldAttachment("Interest on Savings Account (80TTA)","chapter-vi-a")?.comment || ""}
                initialStatus={getFieldAttachment("Interest on Savings Account (80TTA)","chapter-vi-a")?.status || "pending"}
                onViewAttachment={openAttachment}
              />
            )}
          </div>
          <div>
            <FloatingInput
              id="section80TTBNew"
              label="Interest on Deposits (80TTB)"
              placeholder="Interest on Deposits (80TTB)"
              type="number"
              value={investmentDeclaration.sectionVIA.section80TTB || ""}
              onChange={(e) => handleInputChange("sectionVIA", "section80TTB", e.target.value)}
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Deposits (80TTB)", "chapter-vi-a")}
              error={fieldErrors.section80TTBNew}
            />
            {taxRegime === "old" && proofUploadEnabled && (
              <SectionUpload
                sectionName="Interest on Deposits (80TTB)"
                onUpload={(data) => handleDocumentUpload(data, "chapter-vi-a")}
                onRemove={handleRemoveAttachment}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable("Interest on Deposits (80TTB)", "chapter-vi-a")}
                initialAttachmentId={
                  getFieldAttachment(
                    "Interest on Deposits (80TTB)",
                    "chapter-vi-a"
                  )?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(
                    "Interest on Deposits (80TTB)",
                    "chapter-vi-a"
                  )?.fileName
                }
                initialFileSize={
                  getFieldAttachment(
                    "Interest on Deposits (80TTB)",
                    "chapter-vi-a"
                  )?.size
                }
                onViewAttachment={openAttachment}
                initialRejectReason={getFieldAttachment("Interest on Deposits (80TTB)","chapter-vi-a")?.comment || ""}
                initialStatus={getFieldAttachment("Interest on Deposits (80TTB)","chapter-vi-a")?.status || "pending"}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
  const renderHousePropertyDetails = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-2">House Property Details</h3>

      {investmentDeclaration.housePropertyEntries.map((entry, index) => (
        <div key={index} className="p-4 bg-gray-50 rounded-lg mb-4">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center space-between gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`occupancy-${index}`}
                  value="SELF_OCCUPIED"
                  checked={entry.occupancy === "SELF_OCCUPIED"}
                  onChange={(e) =>
                    handleHousePropertyChange(index, "occupancy", e.target.value)
                  }
                  className="mr-2"
                  disabled={
                    isInvestmentInputsReadOnly() &&
                    !isFieldEditable(`House Property - Entry ${index + 1}`, "house-property")
                  }
                />
                Self Occupied Property
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`occupancy-${index}`}
                  value="LET_OUT"
                  checked={entry.occupancy === "LET_OUT"}
                  onChange={(e) =>
                    handleHousePropertyChange(index, "occupancy", e.target.value)
                  }
                  className="mr-2"
                  disabled={
                    isInvestmentInputsReadOnly() &&
                    !isFieldEditable(`House Property - Entry ${index + 1}`, "house-property")
                  }
                />
                Let Out (Rental) Property
              </label>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={entry.loanBeforeApr1999}
                onChange={(e) =>
                  handleHousePropertyChange(index, "loanBeforeApr1999", e.target.checked)
                }
                className="mr-2"
                disabled={
                  isInvestmentInputsReadOnly() &&
                  !isFieldEditable(`House Property - Entry ${index + 1}`, "house-property")
                }
              />
              <span className="text-sm text-gray-700">Loan taken before April 1999</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <FloatingInput
              id={`interestPaidOnBorrowedCapital-${index}`}
              label="Interest paid on borrowed capital"
              placeholder="Interest paid on borrowed capital"
              type="number"
              value={entry.interestPaidOnBorrowedCapital}
              onChange={(e) =>
                handleHousePropertyChange(
                  index,
                  "interestPaidOnBorrowedCapital",
                  e.target.value
                )
              }
              // Interest is the only House Property input that re-opens after
              // submit when its proof is rejected.
              disabled={isInvestmentInputsReadOnly() && !isFieldEditable(`House Property - Entry ${index + 1}`, "house-property")}
              error={fieldErrors[`interestPaidOnBorrowedCapital-${index}`]}
            />
            {entry.occupancy === 'LET_OUT' && (
              <FloatingInput
                id={`rentalIncome-${index}`}
                label="Annual rental income"
                placeholder="Annual rental income"
                type="number"
                value={entry.rentalIncome}
                onChange={(e) =>
                  handleHousePropertyChange(index, 'rentalIncome', e.target.value)
                }
                disabled={
                  isInvestmentInputsReadOnly() &&
                  !isFieldEditable(`House Property - Entry ${index + 1}`, "house-property")
                }
              />
            )}
            <FloatingInput
              id={`propertyAddress-${index}`}
              label="Property Address"
              placeholder="Property Address"
              value={entry.propertyAddress}
              onChange={(e) =>
                handleHousePropertyChange(index, "propertyAddress", e.target.value)
              }
              disabled={
                isInvestmentInputsReadOnly() &&
                !isFieldEditable(`House Property - Entry ${index + 1}`, "house-property")
              }
            />
          </div>

          <div className="mt-4">
            {proofUploadEnabled && (
              <SectionUpload
                sectionName={`House Property - Entry ${index + 1}`}
                onUpload={(data) => handleDocumentUpload(data, "house-property")}
                onRemove={handleRemoveAttachment}
                isSectionLevel={true}
                disabled={isInvestmentInputsReadOnly() && !isFieldEditable(`House Property - Entry ${index + 1}`, "house-property")}
                initialAttachmentId={
                  getFieldAttachment(`House Property - Entry ${index + 1}`, "house-property")?.attachmentId
                }
                initialFileName={
                  getFieldAttachment(`House Property - Entry ${index + 1}`, "house-property")?.fileName
                }
                initialFileSize={
                  getFieldAttachment(`House Property - Entry ${index + 1}`, "house-property")?.size
                }
                initialRejectReason={
                  getFieldAttachment(`House Property - Entry ${index + 1}`, "house-property")?.comment || ""
                }
                initialStatus={
                  getFieldAttachment(`House Property - Entry ${index + 1}`, "house-property")?.status || "pending"
                }
                onViewAttachment={openAttachment}
              />
            )}
          </div>

          {!isReadOnly && index > 0 && (
            <button
              type="button"
              className="mt-2 text-red-600 hover:text-red-800 text-sm"
              onClick={() => removeHousePropertyEntry(index)}
            >
              Remove House Property
            </button>
          )}
        </div>
      ))}
      {/* {!isReadOnly && (
      <button
        type="button"
        onClick={addHousePropertyEntry}
        className="m-2 text-blue-600 hover:text-blue-800 text-sm"
      >
        + Add House Property
      </button>
      )} */}
    </div>
  )

  const renderProofDocuments = () => (
    <div className="space-y-4 mt-6">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Proof Documents</h3>
        {!isReadOnly && proofUploadEnabled && (
        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="text-green-500 flex items-center hover:text-green-600 text-sm"
        >
          <FileUp className="mr-1" size={16} />
          Drop Files Here
        </button>
        )}
      </div>

      <div className="border rounded-lg overflow" style={{ maxHeight: "300px", overflowY: "auto" }}>
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Sr. No.</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Document Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">File Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Last Updated</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Comments</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {uploadedDocuments
              .filter((doc) => doc.sectionId === getCurrentSection()?.id)
              .map((doc, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm align-top">{index + 1}</td>
                <td className="px-4 py-2 text-sm align-top">{doc.documentName}</td>
                <td className="px-4 py-2 text-sm align-top">{doc.originalFileName}</td>
                <td className="px-4 py-2 text-sm align-top">{format(new Date(doc.lastUpdated), "dd-MMM-yyyy")}</td>
                <td className="px-4 py-2 text-sm align-top">{doc.comment}</td>
                <td className="px-4 py-2 text-sm">
                  <div className="flex flex-col gap-2 items-start">
                    {/* Status badge based on document status */}
                    {(() => {
                      const status = doc.status || "uploaded"
                      if (status === "rejected") {
                        return (
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">Rejected</span>
                        )
                      }
                      if (status === "approved") {
                        return (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Approved</span>
                        )
                      }
                      if (status === "pending") {
                        return (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Pending</span>
                        )
                      }
                      return (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Uploaded</span>
                      )
                    })()}

                    {/* When rejected, allow employee to remove after confirmation */}
                    {doc.status === "rejected" && (
                      <>
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => setDocToConfirmRemoveIndex(index)}
                        >
                          Remove rejected document
                        </button>
                        {docToConfirmRemoveIndex === index && (
                          <div className="mt-1 p-2 border border-red-200 bg-red-50 rounded text-xs w-full">
                            <p className="text-red-700 mb-2">
                              This document has been rejected. Are you sure you want to remove it from your list?
                            </p>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                                onClick={async () => {
                                  const attId = doc.attachmentId
                                  if (attId) {
                                    try {
                                      await deleteInvestmentAttachment(attId)
                                    } catch (err) {
                                      console.error("Failed to delete rejected attachment:", err)
                                      alert("Failed to delete attachment. Please try again.")
                                      return
                                    }
                                  }

                                  setUploadedDocuments((prev) => prev.filter((_, i) => i !== index))
                                  // Keep field editable even after removing rejected document until Save & Next
                                  const fieldKey = `${doc.sectionName || ""}-${doc.sectionId || ""}`
                                  if (doc.status === "rejected" && fieldKey) {
                                    setFieldsWithRejectedDocs((prev) => new Set([...prev, fieldKey]))
                                  }
                                  setDocToConfirmRemoveIndex(null)
                                }}
                              >
                                Yes, remove
                              </button>
                              <button
                                type="button"
                                className="px-2 py-1 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50"
                                onClick={() => setDocToConfirmRemoveIndex(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-sm align-top">
                  {doc.attachmentId && (
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => openAttachment(doc.attachmentId, doc.fileName)}
                    >
                      View / Download
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {uploadedDocuments.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                  No documents uploaded yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )

  return (
    <>
    <div className="p-6">
      <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex border-b">
          <button
            className={`py-2 px-4 ${
              activeTab === "investment-declaration"
                ? "bg-gray-100 font-semibold border-b-2 border-blue-500"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("investment-declaration")}
          >
            Investment Declaration
          </button>
        </div>

        {activeTab === "investment-declaration" && (
          <div>
            {/* Financial Year + Tax Regime selection, similar to admin modal */}
            <div className="p-4 border-b bg-gray-50 flex-shrink-0">
              <div className="flex flex-wrap items-center gap-6">
                {shouldShowIncomeTaxGuide && (
                  <button
                    type="button"
                    onClick={openIncomeTaxGuide}
                    className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-700 rounded-none hover:bg-gray-50 transition-colors"
                    title="Open Income Tax Guide"
                  >
                    Income Tax Guide
                  </button>
                )}
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Financial Year:</label>
                  <select
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    value={selectedFinancialYear}
                    onChange={(e) => setSelectedFinancialYear(e.target.value)}
                  >
                    {availableFinancialYears.map((fy) => (
                      <option key={fy} value={fy}>
                        {fy}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">Tax Regime:</label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="taxRegime"
                        value="old"
                        checked={taxRegime === "old"}
                        onChange={(e) => setTaxRegime(e.target.value)}
                        disabled={isTaxRegimeLocked()}
                        className="mr-2"
                      />
                      <span className="text-sm">Old Regime</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="taxRegime"
                        value="new"
                        checked={taxRegime === "new"}
                        onChange={(e) => setTaxRegime(e.target.value)}
                        disabled={isTaxRegimeLocked()}
                        className="mr-2"
                      />
                      <span className="text-sm">New Regime</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            {taxRegime === "old" && (
              <div className="flex border-b border-gray-200">
                {tabs.map((tab, index) => (
                  <button
                    key={tab.id}
                    className={`px-6 py-3 font-medium text-sm transition-colors ${
                      currentSectionIndex === index
                        ? "bg-blue-600 text-white border-b-2 border-blue-600"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                    onClick={() => goToSection(tab.id)}
                  >
                    <div className="flex items-center space-x-2">
                      <span>{tab.label}</span>
                      {savedSections.has(tab.id) && <span className="text-green-500">✓</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Section Progress Indicator */}
            {taxRegime === "old" && (
              <div className="p-3 bg-blue-50 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">
                        Section {currentSectionIndex + 1} of {tabs.length}:
                      </span>
                      <span className="text-sm text-blue-600 font-medium">
                        {getCurrentSection()?.label}
                      </span>
                    </div>

                    {/* Live totals for Section 80C and Chapter VI-A */}
                    {getCurrentSection()?.id === "section-80c" && (
                       <div className="px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-medium flex gap-6">
                        
                        <div>
                          Total 80C:
                          <span className="ml-1">
                            {getSection80CTotal().toLocaleString('en-IN', {
                              maximumFractionDigits: 0,
                            })}
                          </span>
                        </div>

                        <div>
                          Total eligible 80C Exemption:
                          <span className="ml-1">
                            {Math.min(getSection80CTotal(), 150000).toLocaleString('en-IN', {
                              maximumFractionDigits: 0,
                            })}
                          </span>
                        </div>

                      </div>
                    )}
                    {getCurrentSection()?.id === "chapter-vi-a" && (
                      <div className="px-2 py-1 rounded bg-indigo-100 text-indigo-800 text-xs font-medium">
                        Total VI-A: {getViaDeductionsTotal().toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={goToPreviousSection}
                      disabled={currentSectionIndex === 0}
                      className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={goToNextSection}
                      disabled={currentSectionIndex === tabs.length - 1}
                      className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Content Area */}
            <div className="p-4 bg-gray-50">
              <div className="bg-white rounded-lg shadow-sm p-6">
                {taxRegime === "old" ? (
                  <>
                    {getCurrentSection()?.content()}
                    {renderProofDocuments()}
                    <ProofDocumentModal
                      isOpen={isUploadModalOpen}
                      onClose={() => setIsUploadModalOpen(false)}
                      onUpload={handleDocumentUpload}
                    />
                  </>
                ) : showPrevEmploymentTab ? (
                  <div className="space-y-6">
                    {renderPreviousEmployment()}
                    <div className="text-center py-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-green-800 mb-2">New Tax Regime Selected</h3>
                        <p className="text-green-600">
                          No additional deductions applicable under the new tax regime.
                          <br />
                          If you have any tax saving investments, select old tax regime and save your investments.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">New Tax Regime Selected</h3>
                      <p className="text-green-600">
                        No additional deductions applicable under the new tax regime.
                        <br />
                        If you have any tax saving investments, select old tax regime and save your investments.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer Buttons */}
            {(taxRegime === "old" || shouldShowSubmitDeclaration) && (
              <div className="border-t p-4 bg-white flex justify-between items-center flex-shrink-0">
                {taxRegime === "old" ? (
                  <div className="flex space-x-3">
                    <button
                      onClick={handleSaveSection}
                      disabled={isReadOnly}
                      className={`px-4 py-2 rounded flex items-center space-x-2 ${isReadOnly ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-green-500 text-white hover:bg-green-600"}`}
                    >
                      <span>{getCurrentSection()?.id === "house-property" ? "Save" : "Save and Next"}</span>
                    </button>
                    <button
                      onClick={resetCurrentSection}
                      disabled={isReadOnly}
                      className={`px-4 py-2 rounded flex items-center space-x-2 ${isReadOnly ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                    >
                      <span>Reset Section</span>
                    </button>
                  </div>
                ) : (
                  <div />
                )}

                <div className="flex space-x-3">
                  {shouldShowSubmitDeclaration && (
                    <button
                      onClick={handleSubmitDeclaration}
                      disabled={isReadOnly}
                      className={`px-6 py-2 rounded flex items-center space-x-2 ${isReadOnly ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                    >
                      <span>Submit Declaration</span>
                    </button>
                  )}
                  <button
                    onClick={handleCancelClick}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
    <div>
      {/* Cancel warning when Old Regime (nothing declared) or New Regime selected */}
      {cancelConfirmMode && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {cancelConfirmMode === "old_nothing_declared" ? "Cancel without declaring?" : "Cancel - New Regime selected"}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-gray-700">
              {cancelConfirmMode === "old_nothing_declared" ? (
                <>
                  <p>
                    You have chosen <span className="font-semibold">Old Regime</span> but not declared anything.
                    It may lead to tax deduction according to the Old tax regime.
                  </p>
                  <p>Do you still want to cancel?</p>
                </>
              ) : (
                <>
                  <p>
                    You have chosen <span className="font-semibold">New Regime</span>.
                  </p>
                  <p>
                    All data (including attachments) saved under the Old Regime will be <span className="font-semibold">deleted</span> when you proceed.
                  </p>
                  <p>Do you still want to cancel?</p>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3 bg-gray-50">
              <button
                type="button"
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm hover:bg-gray-100"
                onClick={() => setCancelConfirmMode(null)}
              >
                No
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                onClick={() => setCancelConfirmMode(null)}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {submitConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {submitConfirm.mode === "old"
                  ? "Submit Declaration - Old Tax Regime"
                  : "Submit Declaration - New Tax Regime"}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-gray-700">
              {submitConfirm.mode === "old" ? (
                <>
                  <p>
                    Are you sure you want to submit your investment declaration under the
                    <span className="font-semibold"> Old Tax Regime</span>?
                  </p>
                  <p>
                    After submission, you may not be able to edit these details if the
                    investment declaration window is locked by your employer.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    You have selected the <span className="font-semibold">New Tax Regime</span>.
                  </p>
                  <p>
                    All data (including attachments) saved under the Old Regime will be
                    <span className="font-semibold"> deleted</span>, except
                    <span className="font-semibold"> Previous Employment</span> details.
                  </p>
                  <p className="text-red-600">
                    This action cannot be undone. Do you wish to proceed and save under the
                    New Regime?
                  </p>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3 bg-gray-50">
              <button
                type="button"
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm hover:bg-gray-100"
                onClick={() => setSubmitConfirm({ open: false, mode: null })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                onClick={handleConfirmSubmit}
              >
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <AttachmentPreviewModal
      isOpen={attachmentPreview.isOpen}
      url={attachmentPreview.url}
      downloadUrl={attachmentPreview.downloadUrl}
      fileName={attachmentPreview.fileName}
      onClose={() =>
        {
          if (incomeTaxGuideBlobUrlRef.current) {
            URL.revokeObjectURL(incomeTaxGuideBlobUrlRef.current)
            incomeTaxGuideBlobUrlRef.current = null
          }
          setAttachmentPreview({
            isOpen: false,
            url: null,
            downloadUrl: null,
            fileName: "",
          })
        }
      }
    />

    </>
  )
}

const ProofDocumentModal = ({ isOpen, onClose, onUpload }) => {
  const [selectedDocument, setSelectedDocument] = useState("")
  const [file, setFile] = useState(null)
  const [comment, setComment] = useState("")

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile && selectedFile.size <= 10 * 1024 * 1024) {
      // 10MB limit
      setFile(selectedFile)
    } else {
      alert("File size should not exceed 10MB")
    }
  }

  const handleUpload = () => {
    if (selectedDocument && file) {
      onUpload({
        documentName: selectedDocument,
        fileName: file.name,
        size: file.size,
        lastUpdated: new Date().toISOString(),
        comment,
      })
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Upload Document</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <select
              value={selectedDocument}
              onChange={(e) => setSelectedDocument(e.target.value)}
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
            >
              <option value="">Select document type</option>
              <option value="Upload House Rent Details">Upload House Rent Details</option>
              <option value="Upload Section 80C">Upload Section 80C</option>
              <option value="Section 80D">Section 80D</option>
              <option value="Section 80DD">Section 80DD</option>
              <option value="Section 80DDB">Section 80DDB</option>
              <option value="Section 80E">Section 80E</option>
              <option value="Section 80EE">Section 80EE</option>
              <option value="Section 80U">Section 80U</option>
              <option value="Section 80CCD(1B)">Section 80CCD(1B)</option>
              <option value="House Property Documents">House Property Documents</option>
            </select>
            <label className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1">
              Select Document
            </label>
          </div>

          <div className="relative">
            <input
              type="file"
              onChange={handleFileChange}
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
            />
            <label className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1">
              File
            </label>
            <p className="text-xs text-gray-500 mt-1">Max limit for each file is 10 MB</p>
          </div>

          <div className="relative">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
              rows="2"
              placeholder=" "
            ></textarea>
            <label className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1">
              Comment
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleUpload} className="px-4 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600">
            Upload
          </button>
        </div>
      </div>
    </div>
  )
}

